import { Component, OnInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { CommonService } from '../../shared/common.service';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { Folder, Form, Field } from '../folders/folders-data.service';
import { ApiService } from '../../api.service';
import { filter } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-ecrf-template',
  templateUrl: './ecrf-template.component.html',
  styleUrls: ['./ecrf-template.component.scss']
})
export class EcrfTemplateComponent implements OnInit {
  templateId: string;
  template: any;
  loading: boolean = false;
  formCreationLoading: boolean = false;
  newFolderName: string = '';
  newFormName: string = '';
  selectedFolder: string | null = null;
  selectedForm: string | null = null;
  selectedFolderId: string | null = null;
  isSidebarVisible: boolean = true;
  isBuildMode: boolean = true; // Flag to indicate we're in build-ecrf mode
  showCreateFormModal: boolean = false;
  showCreateFieldModal: boolean = false;
  currentForm: any = null;
  form: FormGroup;
  fields: any[] = [];
  isEditMode: boolean = false;
  editingFieldIndex: number | null = null;

  isDragging: boolean = false;
  reviewModalPosition = { top: '60px', right: '70px' };

  // Query, Edit Reason, and Review functionality properties
  tabs = [
    { title: 'Query', type: 'query' },
    { title: 'Field History', type: 'fieldHistory' }
  ];
  fieldHistoryData: any[] = [];
  selectedTab = 0;
  isOptionsModalOpen = false;
  isModalOpen = false;
  isReasonModalOpen = false;
  showReviewModal = false;
  
  fieldLabel: string = '';
  clickedFieldIndex: number | null = null;
  // Instruction table row input modal
  isInstructionTableRowModalOpen = false;
  instructionTableRowValues: { [key: string]: any } = {};
  instructionTableRowField: any = null; // Track which field we're adding rows to
  // Statement modal for fixed-rows-table
  isStatementModalOpen = false;
  statementModalFieldName: string = '';
  statementModalRowIndex: number | null = null; // null for new row, number for editing
  statementModalColumns: any[] = [];
  statementModalValues: { [key: string]: string } = {};
  // Add Rows full-page modal
  showAddRowsModal = false;
  editReason: string = '';
  reviewOptions = {
    dataMonitor: false,
    monitor: false,
    pi: false
  };
  // PI Signature checkbox property - now form-specific
  formReviewStatus: { [formId: string]: { piSignatureReviewed: boolean; reviewOptions: any } } = {};
  queries: { threadId: string; text: string; isClosed: boolean; auditTrail: { user: string; role: string; timestamp: string; message: string }[] }[] = [
    { threadId: this.generateThreadId(), text: '', isClosed: false, auditTrail: [] }
  ];
  newField: any = {
    title: '',
    name: '',
    type: '',
    options: ['Yes', 'No'],
    // Fields are mandatory by default; UI uses `optional` checkbox to invert
    required: true,
    optional: false,
    description: '',
    attachmentRequired: false,
    // Conversion configuration for numeric fields
    conversionRequired: false,
    conversionType: '',
    conversionFromUnit: '',
    conversionToUnit: '',
    columns: [],
    hasDependency: false,
    dependency: {
      folderId: '',
      formId: '',
      fieldId: '',
      triggerValue: ''
    },
    hasValidation: false,
    validation: {
      validationTypes: [] as string[],
      fieldSelection: '',
      dependency: {
        folderId: '',
        formId: '',
        fieldId: '',
        triggerValue: ''
      },
      rangeLimitCheck: { minValue: null as number | string | null, maxValue: null as number | string | null, queryMessage: '' },
      missingDataQueries: { startDate: '', endDate: '', buffer: null as number | null, queryMessage: '' },
      // Legacy single-type (kept for API compat; prefer validationTypes + rangeLimitCheck/missingDataQueries)
      validationType: '',
      minValue: null,
      maxValue: null,
      queryMessage: '',
      startDate: '',
      endDate: '',
      buffer: null
    },
    instructionText: '',
    instructionImages: [],
    instructionColumns: [],
    instructionTableData: [],
    subHeader: ''
  };
  // Maximum date for date inputs (allow future dates)
  maxDate: string = '2100-12-31';
  // Track selected files per field when attachments are enabled
  selectedFiles: { [key: string]: File[] } = {};
  // Temp holder for adding options in modal
  newOptionText: string = '';
  // Dependency-related properties
  availableFolders: any[] = [];
  availableForms: any[] = [];
  availableFields: any[] = [];
  selectedDependencyField: any = null;
  // Validation-related properties
  availableValidationForms: any[] = [];
  availableValidationFields: any[] = [];
  selectedValidationField: any = null;
  // Store dependency field information globally for cross-form dependencies
  dependencyFieldRegistry: { [key: string]: any } = {};
  // Global dependency tracking - stores all dependencies across forms
  globalDependencies: { [key: string]: any[] } = {};
  // Current form values cache for cross-form dependencies
  formValuesCache: { [key: string]: any } = {};
  // Properties for managing comment fields
  commentFields: { [key: string]: any } = {};
  // Default folders from folders-data.service
  defaultFolders: Folder[] = [];
  // Dropdown state management

  // Duplicate form target folder selection
  duplicateFormTargetFolderIds: string[] = [];
  duplicateFormFolderError: string = '';
  showDuplicateFormFolderDropdown: boolean = false;
  /** Source folder id of the form being duplicated (do not change when user selects other target folders). */
  duplicateFormSourceFolderId: string | null = null;

  // Conversion type metadata for numeric fields (length, weight, temperature, etc.)
  conversionTypes = [
    { key: 'length', label: 'Length' },
    { key: 'weight', label: 'Weight' },
    { key: 'temperature', label: 'Temperature' },
    { key: 'time', label: 'Time' },
    { key: 'volume', label: 'Volume' }
  ];

  conversionUnits: { [key: string]: { value: string; label: string }[] } = {
    // Labels mirror the unit-converter dropdown in your screenshots.
    length: [
      { value: 'angstrom', label: 'ångströms [Å]' },
      { value: 'au', label: 'astronomical units [au]' },
      { value: 'barleycorn', label: 'barleycorns' },
      { value: 'cable', label: 'cables' },
      { value: 'cm', label: 'centimeters [cm]' },
      { value: 'chain_survey', label: "chains (surveyors')" },
      { value: 'dm', label: 'decimeters [dm]' },
      { value: 'ell_uk', label: 'ells (UK)' },
      { value: 'pica', label: 'ems (pica)' },
      { value: 'fathom', label: 'fathoms' },
      { value: 'ft', label: 'feet (UK & US)' },
      { value: 'ft_us_survey', label: 'feet (US survey)' },
      { value: 'furlong', label: 'furlongs' },
      { value: 'hand', label: 'hands' },
      { value: 'hm', label: 'hectometers [hm]' },
      { value: 'in', label: 'inches [in]' },
      { value: 'km', label: 'kilometers [km]' },
      { value: 'ly', label: 'light years' },
      { value: 'm', label: 'meters [m]' },
      { value: 'um', label: 'micrometers [µm]' },
      { value: 'mil', label: 'mil' },
      { value: 'mile', label: 'miles (UK & US)' },
      { value: 'nmi_int', label: 'miles (nautical, international)' },
      { value: 'nmi_uk', label: 'miles (nautical, UK)' },
      { value: 'mm', label: 'millimeters [mm]' },
      { value: 'nm', label: 'nanometers [nm]' },
      { value: 'parsec', label: 'parsecs' },
      { value: 'pm', label: 'picometers [pm]' },
      { value: 'scandinavian_mile', label: 'Scandinavian mile' },
      { value: 'thou', label: 'thou' },
      { value: 'yd', label: 'yards [yd]' }
    ],
    weight: [
      { value: 'carat_metric', label: 'carats (metric) [CD]' },
      { value: 'cental', label: 'cental' },
      { value: 'decagram', label: 'decagrams [dag]' },
      { value: 'earth_mass', label: 'Earth masses [M⊕]' },
      { value: 'femtogram', label: 'femtograms [fg]' },
      { value: 'grain', label: 'grains [gr]' },
      { value: 'gram', label: 'grams [g]' },
      { value: 'hectogram', label: 'hectograms [hg]' },
      { value: 'hundredweight', label: 'hundredweights [cwt]' },
      { value: 'kilogram', label: 'kilograms [kg]' },
      { value: 'kilotonne', label: 'kilotonnes [kt]' },
      { value: 'megatonne', label: 'megatonnes [Mt]' },
      { value: 'microgram', label: 'micrograms [µg]' },
      { value: 'milligram', label: 'milligrams [mg]' },
      { value: 'nanogram', label: 'nanograms [ng]' },
      { value: 'ounce', label: 'ounces (US & UK) [oz]' },
      { value: 'ounce_troy', label: 'ounces (troy, precious metals) [oz t]' },
      { value: 'picogram', label: 'picograms [pg]' },
      { value: 'pound', label: 'pounds (US & UK) [lbs]' },
      { value: 'pound_troy', label: 'pounds (troy, precious metals) [lb t]' },
      { value: 'solar_mass', label: 'Solar masses [M☉]' },
      { value: 'slug', label: 'slugs (g-pounds)' },
      { value: 'stone', label: 'stones [st]' },
      { value: 'ton_uk_long', label: 'tons (UK or long) [LT]' },
      { value: 'ton_us_short', label: 'tons (US or short) [ST]' },
      { value: 'tonne', label: 'tonnes (metric) [t]' }
    ],
    temperature: [
      { value: 'celsius', label: 'Celsius [°C]' },
      { value: 'fahrenheit', label: 'Fahrenheit [°F]' },
      { value: 'kelvin', label: 'Kelvin [K]' },
      { value: 'rankine', label: 'Rankine [°R]' },
      { value: 'reaumur', label: 'Réaumure [°Ré]' }
    ],
    time: [
      { value: 'century', label: 'centuries' },
      { value: 'day', label: 'days [d]' },
      { value: 'decade', label: 'decades' },
      { value: 'femtosecond', label: 'femtoseconds [fs]' },
      { value: 'fortnight', label: 'fortnights' },
      { value: 'hour', label: 'hours [h]' },
      { value: 'microsecond', label: 'microseconds [µs]' },
      { value: 'millennium', label: 'millenia' },
      { value: 'millisecond', label: 'milliseconds [ms]' },
      { value: 'minute', label: 'minutes [min]' },
      { value: 'month_common', label: 'months (Common)' },
      { value: 'month_synodic', label: 'months (Synodic)' },
      { value: 'nanosecond', label: 'nanoseconds [ns]' },
      { value: 'picosecond', label: 'picoseconds [ps]' },
      { value: 'quarter_common', label: 'quarters (Common)' },
      { value: 'second', label: 'seconds [s]' },
      { value: 'shake', label: 'shakes' },
      { value: 'week', label: 'weeks' },
      { value: 'year_common', label: 'years (Common) [y]' },
      { value: 'year_avg_gregorian', label: 'years (Average Gregorian)' },
      { value: 'year_julian', label: 'years (Julian)' },
      { value: 'year_leap', label: 'years (Leap)' },
      { value: 'year_tropical', label: 'years (Tropical)' }
    ],
    volume: [
      { value: 'cubic_millimeter', label: 'cubic millimeters' },
      { value: 'cubic_yard', label: 'cubic yards' },
      { value: 'cup', label: 'cups' },
      { value: 'deciliter', label: 'deciliters' },
      { value: 'dram_imperial', label: 'dram (imperial)' },
      { value: 'dram_us', label: 'dram (US)' },
      { value: 'fluid_ounce_imperial', label: 'fluid ounces (imperial) [fl oz]' },
      { value: 'fluid_ounce_us', label: 'fluid ounces (US) [fl oz]' },
      { value: 'gallon_imperial', label: 'gallons (imperial) [gal]' },
      { value: 'gallon_us_dry', label: 'gallons, dry (US)' },
      { value: 'gallon_us_liquid', label: 'gallons, liquid (US) [gal]' },
      { value: 'gill_imperial', label: 'gill (imperial) [gi]' },
      { value: 'gill_us', label: 'gill (US) [gi]' },
      { value: 'kiloliter', label: 'kiloliters [kl]' },
      { value: 'liter', label: 'liters [l or L]' },
      { value: 'liter_1901_1964', label: 'liters (1901-1964)' },
      { value: 'milliliter', label: 'milliliters [ml]' },
      { value: 'microliter', label: 'microliters [µl]' },
      { value: 'nanoliter', label: 'nanoliters [nl]' },
      { value: 'picoliter', label: 'picoliters [pl]' },
      { value: 'pint_imperial', label: 'pints (imperial) [pt]' },
      { value: 'pint_us_dry', label: 'pints, dry (US)' },
      { value: 'pint_us_liquid', label: 'pints, liquid (US) [pt]' },
      { value: 'quart_imperial', label: 'quarts (imperial) [qt]' },
      { value: 'quart_us_dry', label: 'quarts, dry (US)' },
      { value: 'quart_us_liquid', label: 'quarts, liquid (US) [qt]' }
    ]
  };

  showDuplicateFolderModal: boolean = false;
  folderToDuplicate: any = null;
  duplicateFolderName: string = '';
  duplicateFolderNameError: string = '';
  duplicatingFolder: boolean = false;

  showDuplicateFormModal: boolean = false;
  formToDuplicate: any = null;
  duplicateFormName: string = '';
  duplicateFormNameError: string = '';
  duplicatingForm: boolean = false;

  showMoveFormModal: boolean = false;
  formToMove: any = null;
  moveFormTargetFolderId: string | null = null;
  movingForm: boolean = false;

  openDropdowns: Set<string> = new Set();
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public commonService: CommonService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private apiService: ApiService
  ) { }

  // Helper to return a fresh empty field configuration
  private getEmptyFieldConfig(): any {
    return {
      title: '',
      name: '',
      type: '',
      options: ['Yes', 'No'],
      // Fields are mandatory by default; UI uses `optional` checkbox to invert
      required: true,
      optional: false,
      description: '',
      attachmentRequired: false,
      conversionRequired: false,
      conversionType: '',
      conversionFromUnit: '',
      conversionToUnit: '',
      columns: [],
      hasDependency: false,
      dependency: {
        folderId: '',
        formId: '',
        fieldId: '',
        triggerValue: ''
      },
      hasValidation: false,
      validation: {
        validationTypes: [] as string[],
        fieldSelection: '',
        dependency: {
          folderId: '',
          formId: '',
          fieldId: '',
          triggerValue: ''
        },
        rangeLimitCheck: { minValue: null as number | string | null, maxValue: null as number | string | null, queryMessage: '' },
        missingDataQueries: { startDate: '', endDate: '', buffer: null as number | null, queryMessage: '' },
        validationType: '',
        minValue: null,
        maxValue: null,
        queryMessage: ''
      },
      instructionText: '',
      instructionImages: [],
      instructionColumns: [],
      instructionTableData: [],
      subHeader: ''
    };
  }

  private syncRequiredOptionalFlags(field: any): void {
    if (!field) return;
    // If older payloads don't have `optional`, derive it
    if (typeof field.optional !== 'boolean') {
      field.optional = !field.required;
    }
    // Enforce required to be the inverse of optional for consistency
    field.required = !field.optional;
  }

  getUnitsForConversionType(type: string): { value: string; label: string }[] {
    return this.conversionUnits[type] || [];
  }

  onConversionTypeChange(): void {
    // Reset units whenever the type changes so user picks valid combination
    this.newField.conversionFromUnit = '';
    this.newField.conversionToUnit = '';
  }

  private convertFieldValue(field: any, rawValue: any): number | null {
    if (!field || !field.conversionRequired) {
      return null;
    }
    const value = typeof rawValue === 'number' ? rawValue : parseFloat(rawValue);
    if (isNaN(value)) {
      return null;
    }
    const type = (field.conversionType || '').toLowerCase();
    const from = (field.conversionFromUnit || '').toLowerCase();
    const to = (field.conversionToUnit || '').toLowerCase();
    if (!type || !from || !to || from === to) {
      return null;
    }

    if (type === 'weight') {
      // Base unit: kilograms
      const toKg: { [unit: string]: number } = {
        // Metric (SI)
        kilogram: 1,
        gram: 0.001,
        milligram: 1e-6,
        microgram: 1e-9,
        nanogram: 1e-12,
        picogram: 1e-15,
        femtogram: 1e-18,
        hectogram: 0.1,
        decagram: 0.01,
        tonne: 1000,
        kilotonne: 1_000_000,
        megatonne: 1_000_000_000,
        carat_metric: 0.0002,

        // Imperial / US
        pound: 0.45359237,
        ounce: 0.028349523125,
        stone: 6.35029318,
        hundredweight: 50.80234544, // UK long cwt (112 lb)
        ton_uk_long: 1016.0469088,
        ton_us_short: 907.18474,
        slug: 14.59390294,
        grain: 0.00006479891,

        // Troy / precious metals
        ounce_troy: 0.0311034768,
        pound_troy: 0.3732417216,

        // Astronomical
        earth_mass: 5.9722e24,
        solar_mass: 1.98847e30
      };
      if (!toKg[from] || !toKg[to]) {
        return null;
      }
      const valueInKg = value * toKg[from];
      const result = valueInKg / toKg[to];
      return Number.isFinite(result) ? result : null;
    }

    if (type === 'length') {
      // Base unit: meters
      const toMeters: { [unit: string]: number } = {
        // Metric (SI)
        m: 1,
        cm: 0.01,
        dm: 0.1,
        mm: 0.001,
        um: 1e-6,
        nm: 1e-9,
        pm: 1e-12,
        hm: 100,
        km: 1000,

        // Imperial / US
        in: 0.0254,
        ft: 0.3048,
        ft_us_survey: 1200 / 3937, // ≈ 0.3048006096
        yd: 0.9144,
        mile: 1609.344,
        mil: 0.0000254,
        thou: 0.0000254,
        barleycorn: 0.0254 / 3, // 1 barleycorn = 1/3 inch

        // Nautical / surveying / misc
        chain_survey: 20.11684023368064,
        furlong: 201.168,
        hand: 0.1016,
        fathom: 1.8288,
        ell_uk: 1.143,
        cable: 185.2, // nautical cable (international)
        nmi_int: 1852,
        nmi_uk: 1853.184,

        // Astronomy
        angstrom: 1e-10,
        au: 149_597_870_700,
        ly: 9_460_730_472_580_800,
        parsec: 30_856_775_814_913_673
      };
      if (!toMeters[from] || !toMeters[to]) {
        return null;
      }
      const valueInMeters = value * toMeters[from];
      const result = valueInMeters / toMeters[to];
      return Number.isFinite(result) ? result : null;
    }

    if (type === 'temperature') {
      const toCelsius = (val: number, unit: string): number | null => {
        switch (unit) {
          case 'c':
          case 'celsius':
            return val;
          case 'f':
          case 'fahrenheit':
            return (val - 32) * (5 / 9);
          case 'k':
          case 'kelvin':
            return val - 273.15;
          case 'rankine':
          case 'r':
            return (val - 491.67) * (5 / 9);
          case 'reaumur':
            return val * (5 / 4);
          default:
            return null;
        }
      };
      const fromCelsius = (valC: number, unit: string): number | null => {
        switch (unit) {
          case 'c':
          case 'celsius':
            return valC;
          case 'f':
          case 'fahrenheit':
            return valC * (9 / 5) + 32;
          case 'k':
          case 'kelvin':
            return valC + 273.15;
          case 'rankine':
          case 'r':
            return (valC + 273.15) * (9 / 5);
          case 'reaumur':
            return valC * (4 / 5);
          default:
            return null;
        }
      };
      const valueInC = toCelsius(value, from);
      if (valueInC == null) {
        return null;
      }
      const result = fromCelsius(valueInC, to);
      return result != null && Number.isFinite(result) ? result : null;
    }

    if (type === 'time') {
      // Base unit: seconds
      const toSeconds: { [unit: string]: number } = {
        // sub-second
        femtosecond: 1e-15,
        picosecond: 1e-12,
        nanosecond: 1e-9,
        microsecond: 1e-6,
        millisecond: 1e-3,
        shake: 1e-8,

        // base/common
        second: 1,
        minute: 60,
        hour: 3600,
        day: 86400,
        week: 604800,
        fortnight: 1209600,

        // calendar/astronomical approximations
        // Common month: 365.25/12 days (average)
        month_common: 365.25 * 86400 / 12,
        // Synodic month: mean synodic month ≈ 29.530588853 days
        month_synodic: 29.530588853 * 86400,
        // Common quarter: 3 common months
        quarter_common: (365.25 * 86400 / 12) * 3,

        // years
        year_common: 365 * 86400,
        year_avg_gregorian: 365.2425 * 86400,
        year_julian: 365.25 * 86400,
        year_leap: 366 * 86400,
        year_tropical: 365.24219 * 86400,

        // scaled years (use Average Gregorian)
        decade: 10 * 365.2425 * 86400,
        century: 100 * 365.2425 * 86400,
        millennium: 1000 * 365.2425 * 86400
      };

      if (!toSeconds[from] || !toSeconds[to]) {
        return null;
      }
      const valueInSeconds = value * toSeconds[from];
      const result = valueInSeconds / toSeconds[to];
      return Number.isFinite(result) ? result : null;
    }

    if (type === 'volume') {
      // Base unit: liters
      const toLiters: { [unit: string]: number } = {
        // Metric
        liter: 1,
        liter_1901_1964: 1.000028,
        kiloliter: 1000,
        milliliter: 0.001,
        microliter: 1e-6,
        nanoliter: 1e-9,
        picoliter: 1e-12,
        deciliter: 0.1,
        cubic_millimeter: 1e-6,

        // US / imperial volumes (approximate standard factors)
        // Source base: liter and known exact definitions where possible
        fluid_ounce_us: 0.0295735295625,
        fluid_ounce_imperial: 0.0284130625,
        cup: 0.2365882365, // US cup
        pint_us_liquid: 0.473176473,
        pint_us_dry: 0.5506104713575,
        pint_imperial: 0.56826125,
        quart_us_liquid: 0.946352946,
        quart_us_dry: 1.101220942715,
        quart_imperial: 1.1365225,
        gallon_us_liquid: 3.785411784,
        gallon_us_dry: 4.40488377086,
        gallon_imperial: 4.54609,
        dram_us: 0.0036966911953125, // 1 US fl dram = 1/8 US fl oz
        dram_imperial: 0.0035516328125, // 1 imp fl dram = 1/8 imp fl oz
        gill_us: 0.11829411825,
        gill_imperial: 0.1420653125,

        // Volumes from length units
        cubic_yard: 764.554857984
      };

      if (!toLiters[from] || !toLiters[to]) {
        return null;
      }
      const valueInLiters = value * toLiters[from];
      const result = valueInLiters / toLiters[to];
      return Number.isFinite(result) ? result : null;
    }

    return null;
  }

  /** Deep-clone a field without using JSON (avoids parse errors for non-serializable or circular data). */
  private deepCloneField(field: any): any {
    if (field == null) return this.getEmptyFieldConfig();
    const base = this.getEmptyFieldConfig();
    const copy: any = { ...base };
    const keys = Object.keys(field);
    for (const k of keys) {
      const v = field[k];
      if (v === undefined) continue;
      if (v === null) {
        // Normalize null options/columns so UI always has arrays; leave other nulls as-is
        if (k === 'options') { copy.options = base.options; continue; }
        if (k === 'columns') { copy.columns = []; continue; }
        if (k === 'validation') { copy.validation = { ...base.validation }; continue; }
        copy[k] = null;
        continue;
      }
      if (k === 'options') {
        if (Array.isArray(v)) copy.options = [...v];
        else if (typeof v === 'string' && v.trim()) { try { const p = JSON.parse(v); copy.options = Array.isArray(p) ? p : base.options; } catch { copy.options = base.options; } }
        else copy.options = base.options;
        continue;
      }
      if (k === 'columns') {
        copy.columns = Array.isArray(v) ? v.map((col: any) => (col && typeof col === 'object' ? { ...col } : col)) : [];
        continue;
      }
      if (Array.isArray(v)) {
        copy[k] = v.map((item: any) =>
          item != null && typeof item === 'object' && !Array.isArray(item)
            ? { ...item }
            : Array.isArray(item)
              ? item.map((x: any) => (x != null && typeof x === 'object' ? { ...x } : x))
              : item
        );
        continue;
      }
      if (typeof v === 'object') {
        copy[k] = { ...v };
        if (v.dependency) copy[k].dependency = { ...v.dependency };
        if (v.validation) {
          copy[k].validation = { ...v.validation };
          if (v.validation.dependency) copy[k].validation.dependency = { ...v.validation.dependency };
          if (v.validation.validationTypes) copy[k].validation.validationTypes = [...v.validation.validationTypes];
          if (v.validation.rangeLimitCheck) copy[k].validation.rangeLimitCheck = { ...v.validation.rangeLimitCheck };
          if (v.validation.missingDataQueries) copy[k].validation.missingDataQueries = { ...v.validation.missingDataQueries };
        }
        continue;
      }
      copy[k] = v;
    }
    return copy;
  }

  ngOnInit(): void {
    this.templateId = this.route.snapshot.params['id'];
    
    // Validate template ID
    if (!this.templateId) {
      console.error('Template ID is missing from route parameters');
      alert('Invalid template ID. Redirecting to template list.');
      this.router.navigate(['/ecrf-list']);
      return;
    }
    
    console.log('Initializing template component with ID:', this.templateId);
    this.loadDefaultFolders();
    
    // Initialize sidebar visibility based on screen size
    this.initializeSidebarVisibility();
    
    // Listen for URL changes to handle form selection
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.handleUrlChange(event.url);
      });
    // Handle initial URL state
    this.handleUrlChange(this.router.url);
  }

  // Initialize sidebar visibility based on screen size
  private initializeSidebarVisibility(): void {
    if (window.innerWidth > 1200) {
      this.isSidebarVisible = true; // Always visible on large screens
    } else {
      this.isSidebarVisible = false; // Hidden by default on smaller screens
    }
  }

  loadTemplate(formIdToReopen?: string) {
    this.loading = true;
    // First try to get template from API
    this.apiService.getTemplateData(this.templateId).subscribe(
      (response) => {
        try {
          const responseStr = JSON.stringify(response, null, 2);
          if (responseStr.includes('instructionImages') || responseStr.includes('instruction_images')) {
            // Try to find instruction fields in response
            const findInstructionFields = (obj: any, path: string = 'root'): void => {
              if (Array.isArray(obj)) {
                obj.forEach((item, idx) => findInstructionFields(item, `${path}[${idx}]`));
              } else if (obj && typeof obj === 'object') {
                Object.keys(obj).forEach(key => {
                  if (key === 'type' && obj[key] === 'instruction') {
                    
                  }
                  if (key === 'instructionImages' || key === 'instruction_images') {
                    
                  }
                  findInstructionFields(obj[key], `${path}.${key}`);
                });
              }
            };
            findInstructionFields(response);
          } else {
            
          }
        } catch (e) {
          
        }
        
        
        // Try different possible response structures
        this.template = response.template || response.data || response;
        this.normalizeInstructionFieldsInTemplate(this.template);
        
        // If still no template, try to get from the templates list
        if (!this.template) {
          console.warn('Template not found in direct API call, trying templates list...');
          this.apiService.gettemplates().subscribe(
            (templatesResponse) => {
              const templates = templatesResponse.template || templatesResponse.data || templatesResponse;
              if (Array.isArray(templates)) {
                this.template = templates.find((t: any) => 
                  t.id?.toString() === this.templateId || 
                  t.id === this.templateId ||
                  String(t.id) === String(this.templateId)
                );
              }
              
              if (!this.template) {
                console.error('Template not found with ID:', this.templateId);
                alert('Template not found. Please check if the template exists.');
                this.router.navigate(['/ecrf-list']);
                this.loading = false;
                return;
              }
              
              // Initialize folders array if it doesn't exist
              if (!this.template.folders) {
                this.ensureDefaultFoldersExist();
              }
              this.normalizeInstructionFieldsInTemplate(this.template);
              this.loading = false;
              // Reopen form by ID if provided, otherwise handle URL-based form loading
              if (formIdToReopen) {
                const urlParts = this.router.url.split('/');
                const formIndex = urlParts.indexOf('form');
                const formNameFromUrl = formIndex !== -1 ? decodeURIComponent(urlParts[formIndex + 1]) : undefined;
                this.reopenFormById(formIdToReopen, formNameFromUrl);
              } else {
                this.handleUrlChange(this.router.url);
              }
            },
            (listError) => {
              console.error('Error loading templates list:', listError);
              alert('Failed to load template. Please try again.');
              this.router.navigate(['/ecrf-list']);
              this.loading = false;
            }
          );
          return;
        }

        // Initialize folders array if it doesn't exist
        if (!this.template.folders) {
          this.ensureDefaultFoldersExist();
        }
        this.normalizeInstructionFieldsInTemplate(this.template);

        // Debug: Log all instruction fields from template
        console.log('=== CHECKING TEMPLATE FOR INSTRUCTION FIELDS ===');
        if (this.template && this.template.folders) {
          this.template.folders.forEach((folder: any, folderIndex: number) => {
            if (folder.forms) {
              folder.forms.forEach((form: any, formIndex: number) => {
                if (form.fields) {
                  form.fields.forEach((field: any, fieldIndex: number) => {
                    if (field.type === 'instruction') {
                      console.log(`Instruction field found in Folder[${folderIndex}].forms[${formIndex}].fields[${fieldIndex}]:`);
                      console.log('  Field object:', JSON.stringify(field, null, 2));
                      console.log('  instructionImages:', field.instructionImages);
                      console.log('  instructionImages type:', typeof field.instructionImages);
                      console.log('  instructionImages is array:', Array.isArray(field.instructionImages));
                    }
                  });
                }
              });
            }
          });
        }
        console.log('=== END CHECKING TEMPLATE FOR INSTRUCTION FIELDS ===');

        // Add default folders if they don't exist
        this.loading = false;
        // Reopen form by ID if provided, otherwise handle URL-based form loading
        if (formIdToReopen) {
          // Extract form name from URL as fallback
          const urlParts = this.router.url.split('/');
          const formIndex = urlParts.indexOf('form');
          const formNameFromUrl = formIndex !== -1 ? decodeURIComponent(urlParts[formIndex + 1]) : undefined;
          this.reopenFormById(formIdToReopen, formNameFromUrl);
        } else {
          this.handleUrlChange(this.router.url);
        }
      },
      (error) => {
        console.error('Error loading template from API:', error);
        console.log('Template ID that failed:', this.templateId);
        
        // Try to get from templates list as fallback
        this.apiService.gettemplates().subscribe(
          (templatesResponse) => {
            const templates = templatesResponse.template || templatesResponse.data || templatesResponse;
            if (Array.isArray(templates)) {
              this.template = templates.find((t: any) => 
                t.id?.toString() === this.templateId || 
                t.id === this.templateId ||
                String(t.id) === String(this.templateId)
              );
            }
            
            if (!this.template) {
              // Fallback to localStorage
              const savedTemplates = localStorage.getItem('ecrfTemplates');
              if (savedTemplates && savedTemplates.trim()) {
                try {
                  const templates = JSON.parse(savedTemplates);
                  this.template = templates.find((t: any) => 
                    t.id?.toString() === this.templateId || 
                    t.id === this.templateId ||
                    String(t.id) === String(this.templateId)
                  );
                } catch (e) {
                  console.warn('Failed to parse ecrfTemplates from localStorage', e);
                }
              }
            }
            
            if (!this.template) {
              console.error('Template not found in any source with ID:', this.templateId);
              alert('Template not found. Please check if the template exists.');
              this.router.navigate(['/ecrf-list']);
              this.loading = false;
              return;
            }

            // Initialize folders array if it doesn't exist
            if (!this.template.folders) {
              this.ensureDefaultFoldersExist();
            }
            this.normalizeInstructionFieldsInTemplate(this.template);

            // Add default folders if they don't exist
            this.loading = false;
            // Reopen form by ID if provided
            if (formIdToReopen) {
              const urlParts = this.router.url.split('/');
              const formIndex = urlParts.indexOf('form');
              const formNameFromUrl = formIndex !== -1 ? decodeURIComponent(urlParts[formIndex + 1]) : undefined;
              this.reopenFormById(formIdToReopen, formNameFromUrl);
            }
          },
          (listError) => {
            console.error('Error loading templates list:', listError);
            // Final fallback to localStorage
            const savedTemplates = localStorage.getItem('ecrfTemplates');
            if (savedTemplates && savedTemplates.trim()) {
              try {
                const templates = JSON.parse(savedTemplates);
                this.template = templates.find((t: any) => 
                  t.id?.toString() === this.templateId || 
                  t.id === this.templateId ||
                  String(t.id) === String(this.templateId)
                );
              } catch (e) {
                console.warn('Failed to parse ecrfTemplates from localStorage', e);
              }
            }
            
            if (!this.template) {
              alert('Template not found. Please check if the template exists.');
              this.router.navigate(['/ecrf-list']);
            } else {
              // Initialize folders array if it doesn't exist
              if (!this.template.folders) {
                this.ensureDefaultFoldersExist();
              }
              this.loading = false;
              // Reopen form by ID if provided
              if (formIdToReopen) {
                const urlParts = this.router.url.split('/');
                const formIndex = urlParts.indexOf('form');
                const formNameFromUrl = formIndex !== -1 ? decodeURIComponent(urlParts[formIndex + 1]) : undefined;
                this.reopenFormById(formIdToReopen, formNameFromUrl);
              }
            }
          }
        );
      }
    );
  }

  // Helper method to reopen a form by ID after template reload
  reopenFormById(formId: string | number, formName?: string) {
    if (!this.template || !this.template.folders) {
      console.log('Template or folders not loaded yet, will retry...');
      // Retry after a short delay if template not ready
      setTimeout(() => this.reopenFormById(formId, formName), 100);
      return;
    }

    // Convert formId to string for comparison (handles both string and number IDs)
    const formIdStr = String(formId);
    console.log('Attempting to reopen form with ID:', formIdStr, 'and name:', formName);

    // Find the form across all folders by ID (handle both string and number comparisons)
    for (const folder of this.template.folders) {
      if (folder.forms) {
        const form = folder.forms.find((f: any) => {
          const fIdStr = String(f.id);
          return fIdStr === formIdStr || f.id === formId || f.id === Number(formId);
        });
        if (form) {
          console.log('Reopening form by ID:', form.name, 'Form ID:', form.id);
          // Set the folder as selected
          this.selectedFolderId = folder.id;
          this.selectedFolder = folder.name;
          // Open the form and update URL with new name
          this.openForm(form);
          return;
        }
      }
    }
    
    // If not found by ID, try by name as fallback
    if (formName) {
      console.log('Form not found by ID, trying by name:', formName);
      for (const folder of this.template.folders) {
        if (folder.forms) {
          const form = folder.forms.find((f: any) => f.name === formName);
          if (form) {
            console.log('Reopening form by name:', form.name);
            this.selectedFolderId = folder.id;
            this.selectedFolder = folder.name;
            this.openForm(form);
            return;
          }
        }
      }
    }
    
    console.log('Form not found with ID:', formIdStr, 'or name:', formName, 'Available forms:', 
      this.template.folders.flatMap((f: any) => f.forms?.map((form: any) => ({ id: form.id, name: form.name })) || []));
  }

  loadDefaultFolders() {
    this.loading = true;
    // Load template directly since we're now using API data for dependencies
    this.loadTemplate();
  }

  // Query, Edit Reason, and Review functionality methods
  generateThreadId(): string {
    return 'thread_' + Date.now() + '_' + Math.floor(Math.random() * 10000);
  }

  openOptionsModal(event: MouseEvent, target: EventTarget | null, fieldIndex: number) {
    event.stopPropagation();
    this.isOptionsModalOpen = true;
    this.clickedFieldIndex = fieldIndex;
    // Traverse to the closest parent with field title
    const icon = target as HTMLElement;
    // Check for regular form fields
    const fieldGroup = icon.closest('.form-group-wrapper');
    if (fieldGroup) {
      const labelElement = fieldGroup.querySelector('label span');
      if (labelElement) {
        this.fieldLabel = labelElement.textContent || '';
        console.log('Dynamically captured label:', this.fieldLabel);
        return;
      }
    }
    // Check for title fields
    const titleField = icon.closest('.title-field');
    if (titleField) {
      const titleElement = titleField.querySelector('.section-title');
      if (titleElement) {
        this.fieldLabel = titleElement.textContent || '';
        console.log('Dynamically captured title field label:', this.fieldLabel);
        return;
      }
    }
  }

  openModal() {
    this.isModalOpen = true;
    this.selectedTab = 0; // Default to 'Query'
    const fieldName = this.fieldLabel || 'Unknown Field';
    console.log("Opening modal - Fetching field name:", fieldName);
    // For ECRF template, we'll use a simplified approach since we don't have study/subject context
    // In a real implementation, you would fetch from your API
    this.queries = [{
      threadId: this.generateThreadId(),
      text: '',
      isClosed: false,
      auditTrail: []
    }];
  }

  openReviewModal(event?: MouseEvent) {
    if (event) {
      // Calculate position relative to the clicked element
      const target = event.target as HTMLElement;
      const rect = target.getBoundingClientRect();
      
      // Position modal near the clicked button
      this.reviewModalPosition = {
        top: `${rect.top + 2}px`, // 5px below the button
        right: `${window.innerWidth - rect.right}px`
      };
    }
    
    this.showReviewModal = true;
    const fieldName = this.fieldLabel || 'Unknown Field';
    console.log('Opening Review Modal for:', fieldName);
    this.fieldHistoryData = [];
    this.reviewOptions = this.getFormReviewOptions();
  }

  editStudy() {
    // For ECRF template, we'll show edit reason modal for any field that has a value
    const fieldValue = this.getFieldValue(this.fieldLabel);
    if (fieldValue) {
      this.isReasonModalOpen = true;
    }
  }

  getFieldValue(fieldLabel: string): any {
    // Get the current field value based on the field label or name
    const field = this.fields.find(f => f.title === fieldLabel || f.name === fieldLabel);
    if (field && this.form) {
      const formControl = this.form.get(field.name);
      const value = formControl?.value;
      return value;
    }
    return null;
  }

  getCommentFieldValue(fieldName: string, commentFieldName: string): any {
    // For comment fields, we need to store them separately
    const commentKey = `${fieldName}_${commentFieldName}`;
    return this.commentFields[commentKey] || '';
  }

  setCommentFieldValue(fieldName: string, commentFieldName: string, value: any): void {
    const commentKey = `${fieldName}_${commentFieldName}`;
    this.commentFields[commentKey] = value;
  }

  isCommentRequired(fieldName: string): boolean {
    const field = this.fields.find(f => f.name === fieldName);
    if (!field) return false;
    const fieldValue = this.getFieldValue(fieldName);
    return fieldValue === 'no';
  }

  shouldShowNavigation(field: any, navigationType: string): boolean {
    const fieldValue = this.getFieldValue(field.name);
    const titleLower = field.title.toLowerCase();
    // console.log(`shouldShowNavigation check: field="${field.title}", navigationType="${navigationType}", fieldValue="${fieldValue}", titleLower="${titleLower}"`);
    switch (navigationType) {
      case 'adverse-events':
        return titleLower.includes('adverse event') && fieldValue === 'yes';
      case 'laboratory':
        return titleLower.includes('laboratory') && fieldValue === 'yes';
      case 'physical':
        return titleLower.includes('physical') && fieldValue === 'yes';
      case 'idrs':
        return titleLower.includes('idrs') && fieldValue === 'yes';
      default:
        return false;
    }
  }

  validateCommentFields(): boolean {
    let isValid = true;
    this.fields.forEach(field => {
      if (field.type === 'radio' && this.getFieldValue(field.name) === 'no') {
        // Check if comment is required and provided
        if (field.title.toLowerCase().includes('vital signs') && !field.title.toLowerCase().includes('baseline') && !field.title.toLowerCase().includes('follow') && !field.title.toLowerCase().includes('medicine')) {
          const commentKey = `${field.name}_vitalSignsComments`;
          if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
            isValid = false;
          }
        } else if (field.title.toLowerCase().includes('physical') && !field.title.toLowerCase().includes('baseline') && !field.title.toLowerCase().includes('follow')) {
          const commentKey = `${field.name}_physicalExamComments`;
          if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
            isValid = false;
          }
        } else if (field.title.toLowerCase().includes('idrs')) {
          const commentKey = `${field.name}_idrsAssessmentComments`;
          if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
            isValid = false;
          }
        } else if (field.title.toLowerCase().includes('laboratory') && !field.title.toLowerCase().includes('baseline') && !field.title.toLowerCase().includes('follow')) {
          const commentKey = `${field.name}_labInvestigationComments`;
          if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
            isValid = false;
          }
        } else if (field.title.toLowerCase().includes('baseline') && field.title.toLowerCase().includes('vital signs')) {
          const commentKey = `${field.name}_baselineVitalSignsComments`;
          if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
            isValid = false;
          }
        } else if (field.title.toLowerCase().includes('baseline') && field.title.toLowerCase().includes('physical')) {
          const commentKey = `${field.name}_baselinePhysicalExamComments`;
          if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
            isValid = false;
          }
        } else if (field.title.toLowerCase().includes('baseline') && field.title.toLowerCase().includes('laboratory')) {
          const commentKey = `${field.name}_baselineLabInvestigationComments`;
          if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
            isValid = false;
          }
        } else if (field.title.toLowerCase().includes('follow') && field.title.toLowerCase().includes('vital signs')) {
          const commentKey = `${field.name}_followUpVitalSignsComments`;
          if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
            isValid = false;
          }
        } else if (field.title.toLowerCase().includes('follow') && field.title.toLowerCase().includes('physical')) {
          const commentKey = `${field.name}_followUpPhysicalExamComments`;
          if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
            isValid = false;
          }
        } else if (field.title.toLowerCase().includes('follow') && field.title.toLowerCase().includes('laboratory')) {
          const commentKey = `${field.name}_followUpLabInvestigationComments`;
          if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
            isValid = false;
          }
                          } else if (field.title.toLowerCase().includes('medicine') && field.title.toLowerCase().includes('vital signs')) {
                    const commentKey = `${field.name}_medicineVitalSignsComments`;
                    if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
                      isValid = false;
                    }
                  } else if (field.title.toLowerCase().includes('adverse event')) {
                    const commentKey = `${field.name}_adverseEventsComments`;
                    if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
                      isValid = false;
                    }
                  } else if (field.title.toLowerCase().includes('concomitant medication')) {
                    const commentKey = `${field.name}_concomitantMedicationComments`;
                    if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
                      isValid = false;
                    }
                  } else if (field.title.toLowerCase().includes('rescue medication')) {
                    const commentKey = `${field.name}_rescueMedicationComments`;
                    if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
                      isValid = false;
                    }
                  } else if (field.title.toLowerCase().includes('prior medication')) {
                    const commentKey = `${field.name}_priorMedicationComments`;
                    if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
                      isValid = false;
                    }
                  } else if (field.title.toLowerCase().includes('protocol deviation')) {
                    const commentKey = `${field.name}_protocolDeviationComments`;
                    if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
                      isValid = false;
                    }
                  } else if (field.title.toLowerCase().includes('study completion')) {
                    const commentKey = `${field.name}_studyCompletionComments`;
                    if (!this.commentFields[commentKey] || this.commentFields[commentKey].trim() === '') {
                      isValid = false;
                    }
                  }
      }
    });
    return isValid;
  }

  confirmEditReason() {
    if (this.editReason.trim()) {
      // Here you would typically save the edit reason to your backend
      console.log('Edit reason confirmed:', this.editReason);
      console.log('Field:', this.fieldLabel);
      // Close the modal
      this.isReasonModalOpen = false;
      this.editReason = '';
      // You could also update the field value here if needed
    }
  }

  cancelEditReason() {
    this.isReasonModalOpen = false;
    this.editReason = '';
  }

  closeModal() {
    this.isModalOpen = false;
    this.isOptionsModalOpen = false;
    this.clickedFieldIndex = null;
  }

  closeReviewModal() {
    this.showReviewModal = false;
  }

  selectTab(index: number) {
    this.selectedTab = index;
  }

  addQuery(index: number) {
    this.queries.push({
      threadId: this.generateThreadId(),
      text: '',
      isClosed: false,
      auditTrail: []
    });
  }

  confirmSubmitQuery(index: number) {
    const query = this.queries[index];
    if (query.text.trim()) {
      // Add to audit trail
      query.auditTrail.push({
        user: 'Current User', // Replace with actual user
        role: 'User',
        timestamp: new Date().toISOString(),
        message: query.text
      });
      // Here you would typically save to your backend
      console.log('Query submitted:', query);
      // Clear the query text
      query.text = '';
    }
  }

  confirmCloseQuery(index: number) {
    const query = this.queries[index];
    query.isClosed = true;
    // Add to audit trail
    query.auditTrail.push({
      user: 'Current User', // Replace with actual user
      role: 'User',
      timestamp: new Date().toISOString(),
      message: 'Query closed'
    });
    // Here you would typically save to your backend
    console.log('Query closed:', query);
  }

  onReviewCheckboxChange() {
    // Handle review checkbox changes
    console.log('Review options changed:', this.reviewOptions);
    // Save the review options for the current form
    this.setFormReviewOptions(this.reviewOptions);
  }

  // Helper methods for form-specific review status
  getPiSignatureReviewed(): boolean {
    if (!this.currentForm) return false;
    return this.formReviewStatus[this.currentForm.id]?.piSignatureReviewed || false;
  }

  setPiSignatureReviewed(value: boolean): void {
    if (!this.currentForm) return;
    if (!this.formReviewStatus[this.currentForm.id]) {
      this.formReviewStatus[this.currentForm.id] = {
        piSignatureReviewed: false,
        reviewOptions: {
          dataMonitor: false,
          monitor: false,
          pi: false
        }
      };
    }
    this.formReviewStatus[this.currentForm.id].piSignatureReviewed = value;
  }

  getFormReviewOptions(): any {
    if (!this.currentForm) {
      return {
        dataMonitor: false,
        monitor: false,
        pi: false
      };
    }
    return this.formReviewStatus[this.currentForm.id]?.reviewOptions || {
      dataMonitor: false,
      monitor: false,
      pi: false
    };
  }

  setFormReviewOptions(options: any): void {
    if (!this.currentForm) return;
    if (!this.formReviewStatus[this.currentForm.id]) {
      this.formReviewStatus[this.currentForm.id] = {
        piSignatureReviewed: false,
        reviewOptions: {
          dataMonitor: false,
          monitor: false,
          pi: false
        }
      };
    }
    this.formReviewStatus[this.currentForm.id].reviewOptions = { ...options };
  }

  ensureDefaultFoldersExist() {
    console.log('Ensuring default folders exist...');
    console.log('Default folders:', this.defaultFolders);
    console.log('Current template:', this.template);
    if (!this.template.folders) {
      this.template.folders = [];
    }

    // Check if default folders exist, if not add them, if they exist update them with latest fields
    this.defaultFolders.forEach(defaultFolder => {
      console.log('Processing default folder:', defaultFolder.name);
      const existingFolderIndex = this.template.folders.findIndex((folder: any) => 
        folder.name === defaultFolder.name
      );
      if (existingFolderIndex === -1) {
        // Folder doesn't exist, add it
        console.log('Adding new folder:', defaultFolder.name);
        const templateFolder = this.convertDefaultFolderToTemplate(defaultFolder);
        this.template.folders.push(templateFolder);
      } else {
        // Folder exists, update it with latest forms and fields
        console.log('Updating existing folder:', defaultFolder.name);
        const existingFolder = this.template.folders[existingFolderIndex];
        this.syncFolderWithDefaults(existingFolder, defaultFolder);
      }
    });
    console.log('Final template folders:', this.template.folders);
  }

  /**
   * Normalize instruction-type fields so instructionTableData and instructionColumns
   * are always set from backend (handles snake_case or different key casing from API/DB).
   */
  private normalizeInstructionFieldsInTemplate(template: any): void {
    if (!template?.folders) return;
    template.folders.forEach((folder: any) => {
      (folder.forms || []).forEach((form: any) => {
        (form.fields || []).forEach((field: any) => {
          if (field.type !== 'instruction') return;
          const rawTable = field.instructionTableData ?? field.instruction_table_data ?? (() => {
            const key = Object.keys(field).find(k => k.toLowerCase() === 'instructiontabledata');
            return key ? field[key] : undefined;
          })();
          if (rawTable !== undefined) {
            field.instructionTableData = Array.isArray(rawTable) ? rawTable : (typeof rawTable === 'string' && rawTable.trim() ? (() => { try { return JSON.parse(rawTable); } catch { return []; } })() : []);
          } else if (!field.instructionTableData) {
            field.instructionTableData = [];
          }
          const rawCols = field.instructionColumns ?? field.instruction_columns ?? (() => {
            const key = Object.keys(field).find(k => k.toLowerCase() === 'instructioncolumns');
            return key ? field[key] : undefined;
          })();
          if (rawCols !== undefined) {
            field.instructionColumns = Array.isArray(rawCols) ? rawCols : (typeof rawCols === 'string' && rawCols.trim() ? (() => { try { return JSON.parse(rawCols); } catch { return []; } })() : []);
          } else if (!field.instructionColumns) {
            field.instructionColumns = [];
          }
        });
      });
    });
  }

  syncFolderWithDefaults(existingFolder: any, defaultFolder: Folder) {
    // Update folder properties
    existingFolder.icon = defaultFolder.icon;
    existingFolder.color = defaultFolder.color;
    existingFolder.description = defaultFolder.description;
    existingFolder.title = defaultFolder.title;
    existingFolder.subTitle = defaultFolder.subTitle;
    // Sync forms
    defaultFolder.forms.forEach(defaultForm => {
      const existingFormIndex = existingFolder.forms.findIndex((form: any) => 
        form.name === defaultForm.name
      );
      if (existingFormIndex === -1) {
        // Form doesn't exist, add it
        const templateForm = this.convertDefaultFormToTemplate(defaultForm);
        existingFolder.forms.push(templateForm);
      } else {
        // Form exists, update it with latest fields
        const existingForm = existingFolder.forms[existingFormIndex];
        this.syncFormWithDefaults(existingForm, defaultForm);
      }
    });
  }

  syncFormWithDefaults(existingForm: any, defaultForm: Form) {
    // Update form properties
    existingForm.route = defaultForm.route;
    // Sync fields - replace all fields with default fields to ensure we have latest field definitions
    existingForm.fields = defaultForm.fields.map(defaultField => this.convertDefaultFieldToTemplate(defaultField));
  }

  convertDefaultFolderToTemplate(defaultFolder: Folder): any {
    console.log('Converting default folder to template:', defaultFolder.name);
    const convertedFolder = {
      id: Date.now() + '_' + Math.floor(Math.random() * 10000),
      name: defaultFolder.name,
      icon: defaultFolder.icon,
      color: defaultFolder.color,
      description: defaultFolder.description,
      title: defaultFolder.title,
      subTitle: defaultFolder.subTitle,
      createdAt: new Date().toISOString(),
      forms: defaultFolder.forms.map(defaultForm => this.convertDefaultFormToTemplate(defaultForm))
    };
    console.log('Converted folder:', convertedFolder);
    return convertedFolder;
  }

  convertDefaultFormToTemplate(defaultForm: Form): any {
    console.log('Converting default form to template:', defaultForm.name);
    const convertedForm = {
      id: Date.now() + '_' + Math.floor(Math.random() * 10000),
      name: defaultForm.name,
      route: defaultForm.route,
      createdAt: new Date().toISOString(),
      fields: defaultForm.fields.map(defaultField => this.convertDefaultFieldToTemplate(defaultField))
    };
    console.log('Converted form:', convertedForm);
    return convertedForm;
  }

  convertDefaultFieldToTemplate(defaultField: Field): any {
    console.log('Converting default field to template:', defaultField.title);
    const fieldName = defaultField.title.toLowerCase().replace(/\s+/g, '_');
    let convertedField: any = {
      title: defaultField.title,
      name: fieldName,
      type: this.mapFieldType(defaultField.type),
      required: defaultField.required,
      description: defaultField.description,
      attachmentRequired: false
    };
    if (defaultField.values && defaultField.values.length > 0) {
      convertedField.options = defaultField.values.map(v => v.value);
      convertedField.optionLabels = defaultField.values.map(v => v.label);
      convertedField.valueToLabelMap = {};
      defaultField.values.forEach(v => {
        convertedField.valueToLabelMap[v.value] = v.label;
      });
    }
    if (defaultField.unit) {
      convertedField.unit = defaultField.unit;
    }
    if (defaultField.fields && defaultField.fields.length > 0) {
      convertedField.subFields = defaultField.fields.map(subField => this.convertDefaultFieldToTemplate(subField));
    }

    // Table functionality will be recreated from scratch
    console.log('Converted field:', convertedField);
    return convertedField;
  }

  mapFieldType(defaultType: string): string {
    const typeMapping: { [key: string]: string } = {
      'radio-button': 'radio',
      'dropdown': 'select',
      'date': 'date',
      'time': 'time',
      'number': 'number',
      'textarea': 'textarea',
      'checkbox-group': 'checkbox',
      'calculated': 'calculated',
      'compound': 'compound',
      'signature': 'esignature',
      'text': 'text',
      'title': 'title'
    };
    return typeMapping[defaultType] || 'text';
  }

  isDefaultFolder(folderName: string): boolean {
    return this.defaultFolders.some(defaultFolder => defaultFolder.name === folderName);
  }

  createFolder() {
    if (this.newFolderName.trim()) {
      // Check if folder name already exists
      const trimmedName = this.newFolderName.trim();
      const existingFolder = this.template.folders?.find((folder: any) => 
        folder.name.toLowerCase() === trimmedName.toLowerCase()
      );
      if (existingFolder) {
        alert('A folder with this name already exists');
        return;
      }
      if (!this.template.folders) {
        this.template.folders = [];
      }
      const newFolder = {
        id: Date.now(),
        name: this.newFolderName,
        createdAt: new Date().toISOString(),
        forms: []
      };
      this.template.folders.push(newFolder);
      this.apiService.saveFolder(this.templateId, newFolder).subscribe(
        response => {
          console.log(`Data Saved Successfully`);
        }
      )
      this.newFolderName = '';
    }
  }

  onFolderClick(folder: any, event?: MouseEvent): void {
    // Prevent click if we just finished dragging
    if (this.isDragging) {
      return;
    }
    
    console.log('onFolderClick called for folder:', folder.name, 'Current selectedFolderId:', this.selectedFolderId);
    // Toggle folder: if already selected, close it; otherwise, open it
    if (this.selectedFolderId === folder.id) {
      // Folder is already open, close it
      this.closeFolder();
    } else {
      // Folder is closed, open it
      this.selectedFolderId = folder.id;
      this.selectedFolder = folder.name;
    }
    console.log('After onFolderClick - selectedFolderId:', this.selectedFolderId);
  }

  closeFolder() {
    // Method to close the currently selected folder (keeps form open)
    this.selectedFolderId = null;
    this.selectedFolder = null;
    console.log('Folder closed - form remains open');
  }

  openCreateFormModal(folder: any) {
    // Prevent creating forms in locked or frozen folders
    if (folder.is_locked) {
      alert('This folder is locked. No changes are allowed.');
      return;
    }
    if (folder.is_frozen) {
      alert('This folder is frozen. Please unfreeze it to make changes.');
      return;
    }
    this.selectedFolderId = folder.id;
    this.showCreateFormModal = true;
    this.newFormName = '';
  }

  createForm() {
    if (this.newFormName.trim() && this.selectedFolderId) {
      const folder = this.template.folders.find((f: any) => f.id === this.selectedFolderId);
      if (folder) {
        // Check if form name already exists in this folder
        const trimmedName = this.newFormName.trim();
        const existingForm = folder.forms?.find((form: any) => 
          form.name.toLowerCase() === trimmedName.toLowerCase()
        );
        if (existingForm) {
          alert('A form with this name already exists in this folder');
          return;
        }
        const newForm = {
          id: Date.now(),
          name: this.newFormName,
          createdAt: new Date().toISOString(),
          fields: []
        };
        if (!folder.forms) {
          folder.forms = [];
        }
        const Payload = {
          id: this.templateId,
          folder: {
            id: this.selectedFolderId,
            form: newForm
          }
        }

        // Set loading state
        this.formCreationLoading = true;
        this.apiService.createForm(this.templateId, Payload).subscribe(
          response => {
            console.log(response);
            folder.forms.push(newForm);
            this.newFormName = '';
            this.showCreateFormModal = false;
            this.formCreationLoading = false;
          },
          error => {
            console.error('Error creating form:', error);
            // Remove the form from local array if API call failed
            const formIndex = folder.forms.findIndex((f: any) => f.id === newForm.id);
            if (formIndex > -1) {
              folder.forms.splice(formIndex, 1);
            }
            this.formCreationLoading = false;
            alert('Failed to create form. Please try again.');
          }
        )
        // this.saveTemplate();
      }
    }
  }

  SignatureCheck(): boolean {
    if (this.isBuildMode) {
      return false;
    }
    console.log(this.currentForm);
    if (this.isBuildMode) {
      return false;
    }
    if (this.currentForm.name == "END OF SCREENING VISIT" || this.currentForm.name == "END OF BASELINE VISIT" || this.currentForm.name == "END OF THE VISIT") {
      return false;
    }
    return true;
  }

  onFormClick(form: any, event: MouseEvent): void {
    // Prevent click if we just finished dragging
    if (this.isDragging) {
      event.stopPropagation();
      return;
    }
    
    // Stop event propagation to prevent conflicts
    event.stopPropagation();
    // Close all dropdowns immediately
    this.closeAllDropdowns();
    // Small delay to ensure dropdowns are closed before opening form
    setTimeout(() => {
      this.openForm(form);
    }, 10);
  }

  openForm(form: any) {
    console.log('Before opening form - selectedFolderId:', this.selectedFolderId);
    console.log('Before opening form - selectedForm:', this.selectedForm);
    // Close all open dropdowns when switching forms
    this.closeAllDropdowns();
    // Store the current folder state to ensure it's preserved
    const currentFolderId = this.selectedFolderId;
    this.currentForm = form;
    this.fields = form.fields || [];
    // Ensure all fields have field_order property and add Sr. No for fixed-rows-table
    this.fields.forEach((field: any, index: number) => {
      if (field.field_order === undefined) {
        field.field_order = index;
      }
      this.normalizeColumnTypes(field);
      // Ensure Sr. No column exists for fixed-rows-table (frontend only)
      this.ensureSrNoColumnForFixedRowsTable(field);
      // Debug: Log instruction fields and their images
      if (field.type === 'instruction') {
        console.log('🔵 === INSTRUCTION FIELD FOUND ===');
        console.log('🔵 Field ID:', field.id);
        console.log('🔵 Field name:', field.name);
        console.log('🔵 Field title:', field.title);
        console.log('🔵 instructionText:', field.instructionText);
        console.log('🔵 instructionImages:', field.instructionImages);
        console.log('🔵 instructionImages type:', typeof field.instructionImages);
        console.log('🔵 instructionImages is array:', Array.isArray(field.instructionImages));
        if (field.instructionImages && Array.isArray(field.instructionImages)) {
          console.log('🔵 instructionImages length:', field.instructionImages.length);
          field.instructionImages.forEach((img: any, imgIndex: number) => {
            console.log(`  🖼️ Image ${imgIndex}:`, img);
            console.log(`  🖼️ Image ${imgIndex} type:`, typeof img);
            if (typeof img === 'string') {
              console.log(`  🖼️ Image ${imgIndex} URL (string):`, img);
            } else {
              console.log(`  🖼️ Image ${imgIndex} url property:`, img?.url);
              console.log(`  🖼️ Image ${imgIndex} documentLink property:`, img?.documentLink);
              console.log(`  🖼️ Image ${imgIndex} preview property:`, img?.preview);
              console.log(`  🖼️ Image ${imgIndex} name property:`, img?.name);
              console.log(`  🖼️ Image ${imgIndex} full object:`, JSON.stringify(img, null, 2));
            }
          });
        } else {
          console.warn('⚠️ instructionImages is NOT an array or is empty!');
        }
        console.log('🔵 === END INSTRUCTION FIELD ===');
      }
    });
    this.buildForm();
    this.selectedForm = form.name;
    console.log(`Opening Form: ${form.name}`);
    // Ensure folder state is preserved
    this.selectedFolderId = currentFolderId;
    // Update URL to reflect the selected form
    this.updateUrlWithForm(form);
    // Populate fixed-rows-table rows from value column
    this.populateFixedRowsTableFromValue();
    // Populate form with existing data if available
    this.populateFormWithExistingData(form);
    // Process and setup dependencies for the current form
    this.setupFormDependencies(form);
    // Set up dependency field listeners for existing fields
    this.setupDependencyFieldListeners();
    // Initialize form values cache for cross-form dependencies
    this.initializeFormValuesCache();
    console.log('After opening form - selectedFolderId:', this.selectedFolderId);
    console.log('After opening form - selectedForm:', this.selectedForm);
    console.log('Opening form:', {
      id: form.id,
      idType: typeof form.id,
      name: form.name,
      fields: this.fields
    });
  }

  // URL handling methods
  updateUrlWithForm(form: any) {
    const url = `/ecrf-template/${this.templateId}/form/${encodeURIComponent(form.name)}`;
    this.router.navigate([url], { replaceUrl: true });
  }

  handleUrlChange(url: string) {
    console.log('Handling URL change:', url);
    // Parse URL to extract form name
    const urlParts = url.split('/');
    const templateIndex = urlParts.indexOf('ecrf-template');
    const formIndex = urlParts.indexOf('form');
    if (templateIndex !== -1 && formIndex !== -1 && formIndex > templateIndex) {
      const formName = decodeURIComponent(urlParts[formIndex + 1]);
      console.log('Form name from URL:', formName);
      // Find and open the form if it exists
      this.openFormFromUrl(formName);
    } else {
      // No form in URL, clear current form
      this.clearFormSelection();
    }
  }

  openFormFromUrl(formName: string) {
    if (!this.template || !this.template.folders) {
      console.log('Template or folders not loaded yet');
      return;
    }

    // Find the form across all folders
    for (const folder of this.template.folders) {
      if (folder.forms) {
        const form = folder.forms.find((f: any) => f.name === formName);
        if (form) {
          console.log('Found form in folder:', folder.name);
          // Set the folder as selected first
          this.selectedFolderId = folder.id;
          this.selectedFolder = folder.name;
          // Then open the form
          this.openFormWithoutUrlUpdate(form);
          return;
        }
      }
    }
    console.log('Form not found:', formName);
  }

  openFormWithoutUrlUpdate(form: any) {
    console.log('Opening form without URL update:', form.name);
    this.currentForm = form;
    this.fields = form.fields || [];
    // Ensure all fields have field_order property and add Sr. No for fixed-rows-table
    this.fields.forEach((field: any, index: number) => {
      if (field.field_order === undefined) {
        field.field_order = index;
      }
      // Ensure Sr. No column exists for fixed-rows-table (frontend only)
      this.ensureSrNoColumnForFixedRowsTable(field);
      // Debug: Log instruction fields and their images
      if (field.type === 'instruction') {
        console.log('=== INSTRUCTION FIELD FOUND (openFormWithoutUrlUpdate) ===');
        console.log('Field:', field);
        console.log('Field name:', field.name);
        console.log('Field title:', field.title);
        console.log('instructionText:', field.instructionText);
        console.log('instructionImages:', field.instructionImages);
        console.log('instructionImages type:', typeof field.instructionImages);
        console.log('instructionImages is array:', Array.isArray(field.instructionImages));
        if (field.instructionImages && Array.isArray(field.instructionImages)) {
          console.log('instructionImages length:', field.instructionImages.length);
          field.instructionImages.forEach((img: any, imgIndex: number) => {
            console.log(`  Image ${imgIndex}:`, img);
            console.log(`  Image ${imgIndex} type:`, typeof img);
            if (typeof img === 'string') {
              console.log(`  Image ${imgIndex} URL:`, img);
            } else {
              console.log(`  Image ${imgIndex} url property:`, img?.url);
              console.log(`  Image ${imgIndex} documentLink property:`, img?.documentLink);
              console.log(`  Image ${imgIndex} preview property:`, img?.preview);
              console.log(`  Image ${imgIndex} name property:`, img?.name);
            }
          });
        }
        console.log('=== END INSTRUCTION FIELD (openFormWithoutUrlUpdate) ===');
      }
    });
    this.buildForm();
    this.selectedForm = form.name;
    // Populate fixed-rows-table rows from value column
    this.populateFixedRowsTableFromValue();
    // Populate form with existing data if available
    this.populateFormWithExistingData(form);
    // Process and setup dependencies for the current form
    this.setupFormDependencies(form);
    // Set up dependency field listeners for existing fields
    this.setupDependencyFieldListeners();
    // Initialize form values cache for cross-form dependencies
    this.initializeFormValuesCache();
  }

  clearFormSelection() {
    console.log('Clearing form selection');
    this.currentForm = null;
    this.selectedForm = null;
    this.fields = [];
    this.form = new FormGroup({});
  }

  buildForm(): void {
    console.log('Building form with fields:', this.fields);
    const group: any = {};
    for (const field of this.fields) {
      const baseValidators = field.required ? [Validators.required] : [];
      if (field.type === 'title') {
        // Title fields don't need form controls, they are just display elements
        continue;
      } else if (field.type === 'checkbox') {
        const checkboxValidators = field.required ? [this.atLeastOneSelectedValidator()] : [];
        group[field.name] = [[], checkboxValidators];
      } else if (field.type === 'esignature' || field.type === 'signature') {
        group[field.name] = ['', baseValidators];
      } else if (field.type === 'number') {
        const validators = field.required ? [Validators.required, Validators.min(0)] : [Validators.min(0)];
        group[field.name] = [null, validators];
      } else if (field.type === 'compound') {
        if (field.subFields) {
          field.subFields.forEach((subField: any) => {
            const subValidators = subField.required ? [Validators.required] : [];
            group[subField.name] = [null, subValidators];
          });
        }
             } else if (field.type === 'table' || field.type === 'fixed-rows-table') {
         // New table implementation - simple array of objects
         group[field.name] = [[], baseValidators];
      } else {
        group[field.name] = ['', baseValidators];
      }
      if (field.attachmentRequired) {
        group[field.name + '_attachments'] = ['', field.required ? [Validators.required] : []];
      }
      // Auto-created converted value field (read-only) when conversion is configured
      if (field.conversionRequired) {
        const convertedName = `${field.name}_converted`;
        group[convertedName] = [null, []];
      }
    }

    // Add comment fields to the form
    Object.keys(this.commentFields).forEach(commentKey => {
      group[commentKey] = [this.commentFields[commentKey] || '', Validators.required];
    });
    this.form = this.fb.group(group);

    this.fields.forEach(field => {
      if (field.attachmentRequired) {
        this.form.get(field.name + '_attachments')?.disable();
      }
    });
    
    // Add value change listeners for debugging
    this.form.valueChanges.subscribe(values => {
      console.log('Form values changed:', values);
    });
    // Add individual control listeners for all fields to trigger dependency updates
    this.fields.forEach(field => {
      const control = this.form.get(field.name);
      if (control) {
        control.valueChanges.subscribe(value => {
          console.log(`Field "${field.title}" (${field.name}) value changed to:`, value);
          // Check for cross-form dependencies
          this.checkCrossFormDependencies(field.name, value);
          // Trigger change detection for dependency fields
          this.cdr.detectChanges();

          // Update converted value, if configured
          if (field.conversionRequired) {
            const convertedControl = this.form.get(`${field.name}_converted`);
            if (convertedControl) {
              const converted = this.convertFieldValue(field, value);
              if (converted == null) {
                convertedControl.setValue(null, { emitEvent: false });
              } else {
                // Limit to 6 decimal places for readability
                const rounded = Number(parseFloat(String(converted)).toFixed(6));
                convertedControl.setValue(rounded, { emitEvent: false });
              }
            }
          }
        });
      }
    });
    // Set up dependency field listeners
    this.setupDependencyFieldListeners();
    // Set up listeners for any dependency fields that exist in the current form
    this.setupCurrentFormDependencyListeners();
  }

  openCreateFieldModal(): void {
    // Prevent creating fields in locked/frozen forms
    if (this.currentForm && this.currentForm.is_locked) {
      alert('This form is locked. No changes are allowed.');
      return;
    }
    if (this.currentForm && this.currentForm.is_frozen) {
      alert('This form is frozen. Please unfreeze it to make changes.');
      return;
    }
    this.showCreateFieldModal = true;
    this.isEditMode = false;
    this.newField = this.getEmptyFieldConfig();
    this.newField = this.getEmptyFieldConfig();
    // Load available folders for dependency selection
    this.loadAvailableFolders();
  }

  onFieldTypeChange(): void {
    // Set default title to "NOTE" when instruction type is selected
    if (this.newField.type === 'instruction') {
      this.newField.title = 'Instruction/Suggestion';
      // Reset these fields for instruction type
      this.newField.required = false;
      this.newField.optional = true;
      this.newField.attachmentRequired = false;
      this.newField.hasDependency = false;
    }
    // Initialize fixed-rows-table with Sr. No and default Text column
    if (this.newField.type === 'fixed-rows-table') {
      if (!this.newField.columns || this.newField.columns.length === 0) {
        this.newField.columns = [
          {
            title: 'Sr. No',
            type: 'text',
            required: false,
            values: [],
            name: '_sr_no',
            isSrNo: true,
            readOnly: true
          },
          {
            title: '',
            type: 'statement',
            required: true,
            values: [],
            name: '',
            isSecondColumn: true,
            readOnly: true
          }
        ];
      } else {
        // Ensure Sr. No column exists and is first
        const hasSrNo = this.newField.columns.some((col: any) => col.isSrNo || col.name === '_sr_no');
        if (!hasSrNo) {
          // Insert Sr. No at the beginning
          this.newField.columns.unshift({
            title: 'Sr. No',
            type: 'text',
            required: false,
            values: [],
            name: '_sr_no',
            isSrNo: true,
            readOnly: true
          });
        } else {
          // Ensure Sr. No is first
          const srNoIndex = this.newField.columns.findIndex((col: any) => col.isSrNo || col.name === '_sr_no');
          if (srNoIndex > 0) {
            const srNoColumn = this.newField.columns.splice(srNoIndex, 1)[0];
            this.newField.columns.unshift(srNoColumn);
          }
        }
      }
    }
  }

  closeCreateFieldModal(): void {
    this.showCreateFieldModal = false;
    // Reset editing state and discard any unsaved changes
    this.isEditMode = false;
    this.editingFieldIndex = null;
    this.newField = this.getEmptyFieldConfig();
    // Reset editing state and discard any unsaved changes
    this.isEditMode = false;
    this.editingFieldIndex = null;
    this.newField = this.getEmptyFieldConfig();
  }

  // Open Add Rows modal
  openAddRowsModal(): void {
    if (!this.newField.instructionColumns || this.newField.instructionColumns.length === 0) {
      alert('Please add at least one column before adding rows.');
      return;
    }
    this.showAddRowsModal = true;
  }

  // Close Add Rows modal
  closeAddRowsModal(): void {
    this.showAddRowsModal = false;
  }

  addNewField(): void {
    this.syncRequiredOptionalFlags(this.newField);
    // Validate field name
    if (!this.newField.title || !this.newField.title.trim()) {
      alert('Field name is required.');
      return;
    }
    // Validate field type
    if (!this.newField.type) {
      alert('Field type is required.');
      return;
    }
    if (!this.templateId || !this.currentForm) {
      console.error('Template ID or current form not available');
      return;
    }
    // Ensure table fields have at least one column configured
    if ((this.newField.type === 'table' || this.newField.type === 'fixed-rows-table') && (!this.newField.columns || this.newField.columns.length === 0)) {
      alert('Please add at least one column to the table before creating it.');
      return;
    }
    // For fixed-rows-table, validate second column has a title
    if (this.newField.type === 'fixed-rows-table') {
      const secondColumn = this.getSecondColumn(this.newField);
      if (!secondColumn || !secondColumn.title || !secondColumn.title.trim()) {
        alert('The second column (after Sr. No) must have a title. Please enter a title for the second column.');
        return;
      }
    }
    // For fixed-rows-table, ensure Sr. No column exists
    if (this.newField.type === 'fixed-rows-table') {
      const hasSrNo = this.newField.columns && this.newField.columns.some((col: any) => col.isSrNo || col.name === '_sr_no');
      if (!hasSrNo) {
        // Auto-add Sr. No if missing
        this.newField.columns.unshift({
          title: 'Sr. No',
          type: 'text',
          required: false,
          values: [],
          name: '_sr_no',
          isSrNo: true,
          readOnly: true
        });
      }
    }
    const nameSlug = this.newField.title.toLowerCase().replace(/\s+/g, '_');
    console.log("New Field Details for addNewField",this.newField);
    
    // For fixed-rows-table, exclude Sr. No column from columns before sending to backend
    let columnsToSend = this.newField.columns || [];
    if (this.newField.type === 'fixed-rows-table' && columnsToSend.length > 0) {
      columnsToSend = columnsToSend.filter((col: any) => !col.isSrNo && col.name !== '_sr_no');
    }
    
    // Prepare the field data for the API
    const fieldData = {
      title: this.newField.title,
      name: nameSlug,
      type: this.newField.type,
      options: this.newField.options,
      required: this.newField.required,
      description: this.newField.description,
      attachmentRequired: this.newField.attachmentRequired,
      // Persist conversion configuration; backend can ignore if not used
      conversionRequired: this.newField.conversionRequired || false,
      conversionType: this.newField.conversionType || '',
      conversionFromUnit: this.newField.conversionFromUnit || '',
      conversionToUnit: this.newField.conversionToUnit || '',
      columns: columnsToSend,
      dependency: this.newField.dependency,
      hasDependency: this.newField.hasDependency,
      dependencyFieldName: this.newField.hasDependency ? this.getDependencyFieldNameFromSelection() : null,
      hasValidation: this.newField.hasValidation || false,
      validation: this.getValidationForApi(),
      instructionText: this.newField.instructionText || '',
      instructionImages: this.newField.instructionImages || [],
      instructionColumns: this.newField.instructionColumns || [],
      instructionTableData: this.newField.instructionTableData || [],
      subHeader: this.newField.subHeader || ''
    };
    
    // Console log for instruction table data being sent to backend
    if (this.newField.type === 'instruction' && this.newField.instructionColumns && this.newField.instructionColumns.length > 0) {
      console.log('=== INSTRUCTION TABLE DATA BEING SENT TO BACKEND ===');
      console.log('Instruction Columns:', this.newField.instructionColumns);
      console.log('Instruction Table Rows:', this.newField.instructionTableData);
      console.log('Complete Field Data:', fieldData);
      console.log('=== END INSTRUCTION TABLE DATA ===');
    }
    // Prepare the data structure expected by the createField API
    const createFieldData = {
      forms: {
        id: this.currentForm.id,
        fields: fieldData
      }
    };
    console.log('Sending createField data:', createFieldData);
    // Call the API to create the field
    this.loading = true;
    this.apiService.createField(this.templateId, createFieldData.forms).subscribe(
      response => {
        console.log('Field created successfully:', response);
        this.loading = false;
        const currentFormId = this.currentForm?.id;
        const currentFormName = this.currentForm?.name;
        // Add the field to the local fields array and form controls
        const fieldToAdd = {
          ...this.newField,
          name: nameSlug
        };
        this.fields.push(fieldToAdd);
        // Don't add form controls for title or instruction fields (display-only)
        if (this.newField.type === 'title' || this.newField.type === 'instruction') {
          // No form control needed for display-only fields
        } else if (this.newField.type === 'checkbox') {
          const control = this.fb.control([], this.newField.required ? [this.atLeastOneSelectedValidator()] : []);
          this.form.addControl(fieldToAdd.name, control);
        } else if (this.newField.type === 'table' || this.newField.type === 'fixed-rows-table') {
          // New table implementation - simple and clean
          const tableControl = this.fb.control([], this.newField.required ? [Validators.required] : []);
          this.form.addControl(fieldToAdd.name, tableControl);
        } else {
          const control = this.fb.control('', this.newField.required ? [Validators.required] : []);
          this.form.addControl(fieldToAdd.name, control);
        }
        if (this.newField.attachmentRequired) {
          const attachmentControl = this.fb.control('', this.newField.required ? [Validators.required] : []);
          this.form.addControl(fieldToAdd.name + '_attachments', attachmentControl);
        }

        // Set up dependency field listener if this field has a dependency
        if (fieldToAdd.hasDependency && fieldToAdd.dependencyFieldName) {
          const dependencyControl = this.form.get(fieldToAdd.dependencyFieldName);
          if (dependencyControl) {
            dependencyControl.valueChanges.subscribe(value => {
              console.log(`Dependency field "${fieldToAdd.dependencyFieldName}" changed to:`, value);
              this.cdr.detectChanges();
            });
          }
        }
        this.saveFormFields();
        this.closeCreateFieldModal();
        // Refetch template data and keep the form open
        if (currentFormId) {
          this.loadTemplate(String(currentFormId));
        } else if (currentFormName) {
          this.loadTemplate();
          setTimeout(() => {
            if (currentFormName && this.template) {
              this.openFormFromUrl(currentFormName);
            }
          }, 200);
        } else {
          this.loadTemplate();
        }
      },
      error => {
        console.error('Error creating field:', error);
        this.loading = false;
        // Show error message to user
        alert(`Failed to create field: ${error.message || 'Unknown error'}`);
      }
    );
  }

  editField(index: number): void {
    const field = this.fields[index];
    if (!field) {
      console.error('editField: no field at index', index);
      return;
    }
    if (field.is_locked) {
      alert('This field is locked. No changes are allowed.');
      return;
    }
    if (field.is_frozen) {
      alert('This field is frozen. Please unfreeze it to make changes.');
      return;
    }
    this.isEditMode = true;
    this.editingFieldIndex = index;
    // Deep clone the field so changes in the modal don't mutate the live field
    try {
      const str = JSON.stringify(this.fields[index]);
      if (str === undefined || str === '') {
        this.newField = this.deepCloneField(this.fields[index]);
      } else {
        this.newField = JSON.parse(str);
      }
    } catch (e) {
      console.warn('editField: JSON clone failed, using fallback', e);
      this.newField = this.deepCloneField(this.fields[index]);
    }
    // Normalize payload fields that can be null (e.g. esignature with options: null, validation: null)
    if (this.newField.options == null || !Array.isArray(this.newField.options)) {
      this.newField.options = this.newField.type === 'dropdown' || this.newField.type === 'radio' || this.newField.type === 'checkbox' ? ['Yes', 'No'] : [];
    }
    if (this.newField.columns != null && !Array.isArray(this.newField.columns)) {
      this.newField.columns = [];
    }
    // Initialize dependency properties if not present
    if (!this.newField.hasDependency) {
      this.newField.hasDependency = false;
    }
    if (!this.newField.dependency) {
      this.newField.dependency = {
        folderId: '',
        formId: '',
        fieldId: '',
        triggerValue: ''
      };
    }
    // Initialize validation properties if not present
    if (!this.newField.hasValidation) {
      this.newField.hasValidation = false;
    }
    if (!this.newField.validation) {
      this.newField.validation = this.getEmptyValidation();
    }
    this.normalizeValidationShape(this.newField.validation);
    // Initialize instruction properties if not present
    if (this.newField.type === 'instruction') {
      if (!this.newField.instructionText) {
        this.newField.instructionText = '';
      }
      if (!this.newField.instructionImages) {
        this.newField.instructionImages = [];
      }
      if (!this.newField.instructionColumns) {
        this.newField.instructionColumns = [];
      }
      if (!this.newField.instructionTableData) {
        this.newField.instructionTableData = [];
      }
    }
    // Initialize subHeader if not present
    if (!this.newField.subHeader) {
      this.newField.subHeader = '';
    }

    // Backward compatibility: ensure required/optional are synced for UI + payload
    this.syncRequiredOptionalFlags(this.newField);

    // Load available folders for dependency selection
    this.loadAvailableFolders();
    // If editing a field with dependencies, populate the dependency dropdowns
    if (this.newField.hasDependency && this.newField.dependency) {
      this.populateDependencyDropdowns();
    }
    // If editing a field with validation (range-limit + dependent field), populate the validation dropdowns
    if (this.newField.hasValidation && this.newField.validation && this.hasValidationType('range-limit-check') && this.newField.validation.fieldSelection === 'dependent-field') {
      this.populateValidationDropdowns();
    }
    this.showCreateFieldModal = true;
  }

  deleteFieldConfirm(index: number): void {
    const field = this.fields[index];
    if (!field || !this.currentForm || !this.selectedFolderId) {
      alert('Unable to delete field: Missing required information');
      return;
    }

    // Confirm deletion
    const confirmDelete = confirm(`Are you sure you want to delete the field "${field.title}"?`);
    if (!confirmDelete) {
      return;
    }
    const data = {
      field_id: field.id,
      folder_id: this.selectedFolderId,
      form_id: this.currentForm.id
    };
    this.loading = true;
    // Store current form info to reopen it after refetch
    const currentFormId = this.currentForm?.id;
    const currentFormName = this.currentForm?.name;
    const currentUrl = this.router.url; // Preserve current URL which contains form name
    console.log('Storing form info for reopening:', { id: currentFormId, name: currentFormName, url: currentUrl });
    this.apiService.deleteField(this.templateId, field.id).subscribe(
      response => {
        console.log('Field deleted successfully:', response);
        // Close the options modal
        this.isOptionsModalOpen = false;
        this.clickedFieldIndex = null;
        // Refetch template data and keep the form open
        // Pass both ID and name for more reliable reopening
        if (currentFormId) {
          this.loadTemplate(String(currentFormId));
        } else if (currentFormName) {
          // If no ID, try to reopen by name after template loads
          this.loadTemplate();
          setTimeout(() => {
            if (currentFormName && this.template) {
              this.openFormFromUrl(currentFormName);
            }
          }, 200);
        } else {
          // Load template and it will use URL to reopen form
          this.loadTemplate();
        }
        alert('Field deleted successfully');
      },
      error => {
        console.error('Error deleting field:', error);
        this.loading = false;
        alert(`Failed to delete field: ${error.message || 'Unknown error'}`);
      }
    );
  }

  updateField(): void {
    if (this.editingFieldIndex === null) return;
    this.syncRequiredOptionalFlags(this.newField);
    // Validate field name
    if (!this.newField.title || !this.newField.title.trim()) {
      alert('Field name is required.');
      return;
    }
    // Validate field type
    if (!this.newField.type) {
      alert('Field type is required.');
      return;
    }
    // Ensure table fields have at least one column configured when updating
    if ((this.newField.type === 'table' || this.newField.type === 'fixed-rows-table') && (!this.newField.columns || this.newField.columns.length === 0)) {
      alert('Please add at least one column to the table before updating it.');
      return;
    }
    // For fixed-rows-table, validate second column has a title
    if (this.newField.type === 'fixed-rows-table') {
      const secondColumn = this.getSecondColumn(this.newField);
      if (!secondColumn || !secondColumn.title || !secondColumn.title.trim()) {
        alert('The second column (after Sr. No) must have a title. Please enter a title for the second column.');
        return;
      }
    }
    // For fixed-rows-table, ensure Sr. No column exists (only one, at the beginning)
    if (this.newField.type === 'fixed-rows-table') {
      // Remove any existing Sr. No columns first to avoid duplicates
      if (this.newField.columns) {
        this.newField.columns = this.newField.columns.filter((col: any) => !col.isSrNo && col.name !== '_sr_no');
      } else {
        this.newField.columns = [];
      }
      // Add Sr. No column at the beginning
      this.newField.columns.unshift({
        title: 'Sr. No',
        type: 'text',
        required: false,
        values: [],
        name: '_sr_no',
        isSrNo: true,
        readOnly: true
      });
    }
    // const updatedField = {
    //   ...this.newField
    //   name: this.newField.title.toLowerCase().replace(/\s+/g, '_'),
    //   // Update dependency field information
    //   dependencyFieldName: this.newField.hasDependency ? this.getDependencyFieldNameFromSelection() : null
    // };
    console.log("Field", this.fields[this.editingFieldIndex]);
    console.log(this.newField);
    
    // For fixed-rows-table, exclude Sr. No column from columns before sending to backend
    let columnsToSend = this.newField.columns || [];
    if (this.newField.type === 'fixed-rows-table' && columnsToSend.length > 0) {
      columnsToSend = columnsToSend.filter((col: any) => !col.isSrNo && col.name !== '_sr_no');
    }
    
    const updatedField = (this.newField.type === 'table' || this.newField.type === 'fixed-rows-table') 
    ? {
      id: this.fields[this.editingFieldIndex].id,
      name: this.newField.title.toLowerCase().replace(/\s+/g, '_'),
      title: this.newField.title,
      description: this.newField.description,
      type: this.newField.type,
      required: this.newField.required,
      attachmentRequired: this.newField.attachmentRequired,
      columns: columnsToSend,
      dependency: this.newField.dependency ? this.newField.dependency : null,
      dependencyFieldName: this.newField.hasDependency ? this.getDependencyFieldNameFromSelection() : null,
      hasValidation: this.newField.hasValidation || false,
      validation: this.getValidationForApi(),
      subHeader: this.newField.subHeader || ''
    }
    : {
      // Don't send UI-only flags like `optional` to backend
      ...((
        ({ optional, ...rest }) => rest
      )(this.newField)),
      name: this.newField.title.toLowerCase().replace(/\s+/g, '_'),
      dependencyFieldName: this.newField.hasDependency ? this.getDependencyFieldNameFromSelection() : null,
      hasValidation: this.newField.hasValidation || false,
      validation: this.getValidationForApi(),
      instructionColumns: this.newField.instructionColumns || [],
      instructionTableData: this.newField.instructionTableData || []
    }
    // Console log for instruction table data being sent to backend during edit
    if (this.newField.type === 'instruction' && this.newField.instructionColumns && this.newField.instructionColumns.length > 0) {
      console.log('=== INSTRUCTION TABLE DATA BEING SENT TO BACKEND (EDIT) ===');
      console.log('Instruction Columns:', this.newField.instructionColumns);
      console.log('Instruction Table Rows:', this.newField.instructionTableData);
      console.log('Updated Field Data:', updatedField);
      console.log('=== END INSTRUCTION TABLE DATA (EDIT) ===');
    }
    
    const forms = {
      id: this.currentForm.id,
      fields: updatedField
    }
    const oldFieldName = this.fields[this.editingFieldIndex].name;
    const oldFieldType = this.fields[this.editingFieldIndex].type;
    let edit_field_status = false;
    console.log("Updated_Field:" ,updatedField);
    // Use appropriate route based on field type
    const updateObservable = (updatedField.type === 'table' || updatedField.type === 'fixed-rows-table') 
      ? this.apiService.updateTablecolumns(this.templateId, forms)
      : this.apiService.editField(this.templateId, forms);
    updateObservable.subscribe(
      error => {
        console.error('Error updating field:', error);
      }
    );
    // For fixed-rows-table, ensure Sr. No column exists in frontend after update
    if (updatedField.type === 'fixed-rows-table') {
      this.ensureSrNoColumnForFixedRowsTable(updatedField);
    }
    
    this.fields[this.editingFieldIndex] = updatedField;
    // Remove old form controls
    if (oldFieldName !== updatedField.name) {
      this.form.removeControl(oldFieldName);
      this.form.removeControl(oldFieldName + '_attachments');
    }

    // Table functionality will be recreated from scratch
    // Add new form controls based on field type
    // Don't add form controls for title or instruction fields (display-only)
    if (updatedField.type === 'title' || updatedField.type === 'instruction') {
      // No form control needed for display-only fields
    } else if (updatedField.type === 'checkbox') {
      const control = this.fb.control([], updatedField.required ? [this.atLeastOneSelectedValidator()] : []);
      this.form.addControl(updatedField.name, control);
    } else if (updatedField.type === 'table' || updatedField.type === 'fixed-rows-table') {
      // Table functionality will be recreated from scratch
      const tableControl = this.fb.control([], updatedField.required ? [Validators.required] : []);
      this.form.addControl(updatedField.name, tableControl);
    } else {
      const control = this.fb.control('', updatedField.required ? [Validators.required] : []);
      this.form.addControl(updatedField.name, control);
    }
    if (updatedField.attachmentRequired) {
      const attachmentControl = this.fb.control('', updatedField.required ? [Validators.required] : []);
      this.form.addControl(updatedField.name + '_attachments', attachmentControl);
    }
    this.saveFormFields();
    this.isEditMode = false;
    this.editingFieldIndex = null;
    this.closeCreateFieldModal();
  }

  deleteField(index: number): void {
    const field = this.fields[index];
    if (field && field.is_locked) {
      alert('This field is locked. No changes are allowed.');
      return;
    }
    if (field && field.is_frozen) {
      alert('This field is frozen. Please unfreeze it to make changes.');
      return;
    }
    this.fields.splice(index, 1);
    this.form.removeControl(field.name);
    this.form.removeControl(field.name + '_attachments');
    this.saveFormFields();
  }

  // File handling
  onFileSelected(event: any, fieldName: string): void {
    const files = event.target.files as FileList | null;
    if (files && files.length > 0) {
      this.selectedFiles[fieldName] = Array.from(files);
      this.form.get(fieldName + '_attachments')?.setValue(files);
    } else {
      this.selectedFiles[fieldName] = [];
      this.form.get(fieldName + '_attachments')?.setValue('');
    }
  }

  removeFile(fieldName: string, index: number): void {
    if (this.selectedFiles[fieldName]) {
      this.selectedFiles[fieldName].splice(index, 1);
      if (this.selectedFiles[fieldName].length === 0) {
        this.form.get(fieldName + '_attachments')?.setValue('');
      }
    }
  }

  getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'fa fa-file-pdf-o';
      case 'doc':
      case 'docx':
        return 'fa fa-file-word-o';
      case 'xls':
      case 'xlsx':
        return 'fa fa-file-excel-o';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'fa fa-file-image-o';
      default:
        return 'fa fa-file-o';
    }
  }

  // Method to open/view attached files
  openFile(file: File): void {
    if (!file) {
      console.error('No file provided');
      return;
    }

    // Create a URL for the file
    const fileUrl = URL.createObjectURL(file);
    // Open the file in a new tab/window
    const newWindow = window.open(fileUrl, '_blank');
    // Clean up the URL after a delay to free memory
    setTimeout(() => {
      URL.revokeObjectURL(fileUrl);
    }, 1000);
    // If popup was blocked, show an alert with instructions
    if (!newWindow) {
      alert('Popup blocked. Please allow popups for this site to view files, or right-click the file and select "Open in new tab".');
    }
  }

  // Method to download attached files
  downloadFile(file: File): void {
    if (!file) {
      console.error('No file provided');
      return;
    }

    // Create a URL for the file
    const fileUrl = URL.createObjectURL(file);
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Clean up the URL
    URL.revokeObjectURL(fileUrl);
  }

  // Options editing
  addOption(): void {
    const value = (this.newOptionText || '').trim();
    if (!value) return;
    if (!this.newField.options) {
      this.newField.options = [];
    }
    this.newField.options = [...this.newField.options, value];
    this.newOptionText = '';
  }

  removeOption(index: number): void {
    if (!this.newField.options) return;
    this.newField.options = this.newField.options.filter((_, i) => i !== index);
  }

  // Instruction image handling
  onInstructionImageSelected(event: any): void {
    const files = event.target.files as FileList | null;
    if (files && files.length > 0) {
      if (!this.newField.instructionImages) {
        this.newField.instructionImages = [];
      }
      Array.from(files).forEach((file: File) => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e: any) => {
            const imageData = {
              name: file.name,
              file: file,
              preview: e.target.result,
              url: null as string | null
            };
            this.newField.instructionImages = [...(this.newField.instructionImages || []), imageData];
          };
          reader.readAsDataURL(file);
        }
      });
      // Reset the input
      event.target.value = '';
    }
  }

  removeInstructionImage(index: number): void {
    if (this.newField.instructionImages) {
      this.newField.instructionImages = this.newField.instructionImages.filter((_, i) => i !== index);
    }
  }

  openImageModal(imageUrl: string): void {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  }

  getInstructionImageSrc(image: any): string {
    console.log('🔍 getInstructionImageSrc called with:', image);
    console.log('🔍 Image type:', typeof image);
    if (typeof image === 'string') {
      console.log('✅ Image is string URL, returning:', image);
      return image;
    }
    // Handle attachment-like structure from backend (url, documentLink, etc.)
    const src = image?.url || image?.documentLink || image?.preview || image || '';
    console.log('✅ Image is object, extracted src:', src);
    console.log('📋 Image object properties:', {
      hasUrl: !!image?.url,
      url: image?.url,
      hasDocumentLink: !!image?.documentLink,
      documentLink: image?.documentLink,
      hasPreview: !!image?.preview,
      preview: image?.preview,
      hasName: !!image?.name,
      name: image?.name,
      fullObject: image
    });
    if (!src) {
      console.warn('⚠️ WARNING: No valid image source found!');
    } else {
      console.log('✅ Final image src URL:', src);
    }
    return src;
  }

  getInstructionImageAlt(image: any): string {
    if (typeof image === 'string') {
      // Extract filename from URL if it's a string
      return this.extractFileNameFromUrl(image);
    }
    return image?.name || 'Instruction image';
  }

  hasImageName(image: any): boolean {
    if (typeof image === 'string') {
      // If it's a URL string, try to extract filename
      const fileName = this.extractFileNameFromUrl(image);
      return fileName !== 'Instruction image';
    }
    return !!image?.name;
  }

  logInstructionField(field: any): boolean {
    console.log('🟢 === RENDERING INSTRUCTION FIELD IN TEMPLATE ===');
    console.log('🟢 Field ID:', field.id);
    console.log('🟢 Field name:', field.name);
    console.log('🟢 Field title:', field.title);
    console.log('🟢 Field type:', field.type);
    console.log('🟢 instructionText:', field.instructionText);
    console.log('🟢 instructionImages:', field.instructionImages);
    console.log('🟢 instructionImages type:', typeof field.instructionImages);
    console.log('🟢 instructionImages is array:', Array.isArray(field.instructionImages));
    if (field.instructionImages) {
      console.log('🟢 instructionImages length:', field.instructionImages.length);
      if (Array.isArray(field.instructionImages) && field.instructionImages.length > 0) {
        field.instructionImages.forEach((img: any, idx: number) => {
          console.log(`  🖼️ Template Image ${idx}:`, img);
          if (typeof img === 'object' && img.url) {
            console.log(`  🖼️ Template Image ${idx} URL:`, img.url);
          }
        });
      }
    } else {
      console.warn('⚠️ instructionImages is null/undefined in template!');
    }
    console.log('🟢 === END RENDERING INSTRUCTION FIELD ===');
    return true; // Always return true to render the field
  }

  checkInstructionImages(field: any): boolean {
    const hasImages = field.instructionImages && field.instructionImages.length > 0;
    console.log('🟡 checkInstructionImages for field:', field.name, '(ID:', field.id, ')');
    console.log('🟡   instructionImages:', field.instructionImages);
    console.log('🟡   hasImages:', hasImages);
    if (hasImages) {
      console.log('🟡   ✅ Images will be displayed');
      console.log('🟡   Number of images:', field.instructionImages.length);
      field.instructionImages.forEach((img: any, idx: number) => {
        const src = this.getInstructionImageSrc(img);
        console.log(`🟡   Image ${idx} will use src:`, src);
      });
    } else {
      console.log('🟡   ❌ No images to display');
      if (!field.instructionImages) {
        console.log('🟡   Reason: instructionImages is null/undefined');
      } else if (!Array.isArray(field.instructionImages)) {
        console.log('🟡   Reason: instructionImages is not an array, type:', typeof field.instructionImages);
      } else {
        console.log('🟡   Reason: instructionImages array is empty');
      }
    }
    return hasImages;
  }

  getInstructionImageName(image: any): string {
    if (typeof image === 'string') {
      return this.extractFileNameFromUrl(image);
    }
    return image?.name || 'Instruction image';
  }

  extractFileNameFromUrl(url: string): string {
    if (!url) return 'Instruction image';
    try {
      // Remove query parameters
      const sanitizedPath = url.split('?')[0];
      // Get the last segment of the path
      const segments = sanitizedPath.split('/');
      const fileName = segments.pop() || sanitizedPath;
      // Return filename or default
      return fileName || 'Instruction image';
    } catch {
      return 'Instruction image';
    }
  }

  toggleCheckbox(fieldName: string, option: string, checked: boolean): void {
    const control = this.form.get(fieldName);
    if (!control) return;
    const current: string[] = Array.isArray(control.value) ? [...control.value] : [];
    if (checked) {
      if (!current.includes(option)) current.push(option);
    } else {
      const idx = current.indexOf(option);
      if (idx !== -1) current.splice(idx, 1);
    }
    control.setValue(current);
    control.markAsTouched();
  }

  formatFieldTitle(title: string): string {
    if (!title) return title;
    // Define unit mappings - handle uppercase to proper case
    const unitMappings: { [key: string]: string } = {
      '(KG)': '(Kg)',
      '(CM)': '(Cm)',
      '(MM)': '(Mm)',
      '(ML)': '(Ml)',
      '(MG)': '(Mg)',
    };
    let formattedTitle = title;
    Object.keys(unitMappings).forEach((key)=>{
      formattedTitle = formattedTitle.replace(key, unitMappings[key]);
    });
    return formattedTitle;
  }

  private atLeastOneSelectedValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (Array.isArray(value) && value.length > 0) return null;
      return { required: true };
    };
  }

  onSubmit(): void {
    console.log('Form submitted:', this.form.value);
  }

  saveFormFields(): void {
    if (this.currentForm) {
      this.currentForm.fields = this.fields;
      // this.saveTemplate();
    }
  }

  doneFields(): void {
    if (!this.currentForm || !this.templateId || !this.fields || this.fields.length === 0) {
      console.error('Cannot submit fields: missing template, form, or fields');
      return;
    }

    // Prepare the payload structure as specified
    const payload = {
      template_id: this.templateId,
      form_id: this.currentForm.id.toString(),
      fields: this.fields.map((field: any) => {
        const fieldPayload: any = {
          id: field.id ? field.id.toString() : (field.field_id ? field.field_id.toString() : null),
          name: field.name || ''
        };

        // For fixed-rows-table, include row data
        if (field.type === 'fixed-rows-table') {
          const tableControl = this.form.get(field.name);
          if (tableControl) {
            const tableData = tableControl.value || [];
            // Exclude Sr. No column from row data before sending
            const rowsData = tableData.map((row: any) => {
              const rowData: any = {};
              // Get columns excluding Sr. No
              const columnsToInclude = this.getTableColumnsExcludingSrNo(field);
              columnsToInclude.forEach((column: any, index: number) => {
                // Find the original index in the full columns array (excluding Sr. No)
                const originalIndex = field.columns ? field.columns.indexOf(column) : -1;
                // Use column name if available, otherwise generate key based on position
                // For fixed-rows-table, column keys are typically: col_0, col_1, etc. (excluding Sr. No)
                const columnKey = column.name || (originalIndex > 0 ? `col_${originalIndex - 1}` : `col_${index}`);
                // Get value from row using the column key
                rowData[columnKey] = row[columnKey] !== undefined ? row[columnKey] : null;
              });
              return rowData;
            });
            fieldPayload.rows = rowsData;
          }
        }

        return fieldPayload;
      }).filter((field: any) => field.name) // Filter out fields without names
    };

    console.log('Submitting fields:', payload);

    this.apiService.submitFields(payload).subscribe(
      response => {
        console.log('Fields submitted successfully:', response);
        alert('Fields saved successfully!');
      },
      error => {
        console.error('Error submitting fields:', error);
        alert('Failed to save fields. Please try again.');
      }
    );
  }

  toggleSideNav(): void {
    this.isSidebarVisible = !this.isSidebarVisible;
  }

  // Initialize sidebar visibility based on screen size
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (event.target.innerWidth > 1200) {
      this.isSidebarVisible = true; // Always visible on large screens
    }
  }

  private saveTemplate() {
    // Save to API first
    this.apiService.saveTemplates(this.template).subscribe(
      (response) => {
        console.log('Template saved to API successfully:', response);
        // Also update localStorage as backup
        const savedTemplates = localStorage.getItem('ecrfTemplates');
        if (savedTemplates && savedTemplates.trim()) {
          try {
            const templates = JSON.parse(savedTemplates);
            const index = templates.findIndex((t: any) => t.id.toString() === this.templateId);
            if (index !== -1) {
              templates[index] = this.template;
              localStorage.setItem('ecrfTemplates', JSON.stringify(templates));
            }
          } catch (e) {
            console.warn('Failed to parse ecrfTemplates from localStorage', e);
          }
        }
      },
      (error) => {
        console.error('Error saving template to API:', error);
        // Fallback to localStorage only
        const savedTemplates = localStorage.getItem('ecrfTemplates');
        if (savedTemplates && savedTemplates.trim()) {
          try {
            const templates = JSON.parse(savedTemplates);
            const index = templates.findIndex((t: any) => t.id.toString() === this.templateId);
            if (index !== -1) {
              templates[index] = this.template;
              localStorage.setItem('ecrfTemplates', JSON.stringify(templates));
            }
          } catch (e) {
            console.warn('Failed to parse ecrfTemplates from localStorage', e);
          }
        }
      }
    );
  }

  // Table functionality will be recreated from scratch
  // Navigation methods for conditional navigation
  navigateToAdverseEvents(): void {
    this.router.navigate(['/folders/adverse-events/adverse-events'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId,
        formName: 'adverse-event-report'
      }
    });
  }

  navigateToLaboratory(): void {
    this.router.navigate(['/folders/laboratory-investigation-log/laboratory-investigation-log'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId 
      }
    });
  }

  navigateToPhysicalSystemic(): void {
    this.router.navigate(['/folders/physical-systemic/physical-systemic'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId 
      }
    });
  }

  navigateToIDRS(): void {
    this.router.navigate(['/folders/idrs/idrs-form-log'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId 
      }
    });
  }

  navigateToPriorMedication(): void {
    this.router.navigate(['/folders/prior-medication-log/prior-medication-log'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId,
        formName: 'prior-medication-log'
      }
    });
  }

  navigateToConcomitantMedication(): void {
    this.router.navigate(['/folders/concomitant-medication-log/concomitant-medication-log'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId,
        formName: 'concomitant-medication-log'
      }
    });
  }

  navigateToRescueMedication(): void {
    this.router.navigate(['/folders/rescue-medication-log/rescue-medication-log'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId,
        formName: 'rescue-medication-log'
      }
    });
  }

  navigateToProtocolDeviation(): void {
    this.router.navigate(['/folders/protocol-deviation/protocol-deviation'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId,
        formName: 'protocol-deviation-log'
      }
    });
  }

  navigateToStudyCompletion(): void {
    this.router.navigate(['/folders/study-completion-termination/study-completion-termination'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId,
        formName: 'study-completion-termination'
      }
    });
  }

  navigateToMedicalHistory(): void {
    this.router.navigate(['/folders/medical-history/medical-history'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId 
      }
    });
  }

  // Additional navigation methods for baseline, follow-up, and medicine dispensing
  navigateToBaselinePhysicalExam(): void {
    this.router.navigate(['/folders/baseline-visits/baseline-visits'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId,
        formName: 'baseline-physical-exam'
      }
    });
  }

  navigateToBaselineLabInvestigation(): void {
    this.router.navigate(['/folders/baseline-visits/baseline-visits'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId,
        formName: 'baseline-lab-investigation'
      }
    });
  }

  navigateToFollowUpPhysical(): void {
    this.router.navigate(['/folders/follow-up-visit/follow-up-visit'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId,
        formName: 'follow-up-physical'
      }
    });
  }

  navigateToFollowUpLabInvestigation(): void {
    this.router.navigate(['/folders/follow-up-visit/follow-up-visit'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId,
        formName: 'follow-up-laboratory-investigation'
      }
    });
  }

  navigateToVitalSigns(): void {
    this.router.navigate(['/folders/vital-signs/vital-signs'], {
      queryParams: { 
        studyId: this.templateId, 
        subjectId: this.templateId,
        formName: 'vital-signs'
      }
    });
  }

  // Helper method to check if a field value is "yes"
  isFieldValueYes(fieldName: string): boolean {
    const field = this.fields.find(f => f.name === fieldName);
    if (field && this.form) {
      const value = this.form.get(fieldName)?.value;
      return value === 'yes' || value === 'Yes';
    }
    return false;
  }

  // Navigation method to go back to build templates page
  navigateToBuildTemplates(): void {
    this.router.navigate(['/ecrf-list']);
  }

  // Folder management methods
  editFolder(folder: any): void {
    if (folder.is_locked) {
      alert('This folder is locked. No changes are allowed.');
      return;
    }
    if (folder.is_frozen) {
      alert('This folder is frozen. Please unfreeze it to make changes.');
      return;
    }
    const newName = prompt('Enter new folder name:', folder.name);
    if (newName && newName.trim() && newName !== folder.name) {
      folder.name = newName.trim();
      // this.saveTemplate();
      this.apiService.editFolder(this.templateId, folder).subscribe(
        response => {
          console.log(response);
        }
      );
    }
  }

  deleteFolder(folder: any): void {
    if (folder.is_locked) {
      alert('This folder is locked. No changes are allowed.');
      return;
    }
    if (folder.is_frozen) {
      alert('This folder is frozen. Please unfreeze it to make changes.');
      return;
    }
    if (confirm(`Are you sure you want to delete the folder "${folder.name}"? This will also delete all forms inside it.`)) {
      const folderIndex = this.template.folders.findIndex((f: any) => f.id === folder.id);
      if (folderIndex > -1) {
        this.template.folders.splice(folderIndex, 1);
        this.apiService.deleteFolder(this.templateId, folder).subscribe(
          response => {
            console.log(response);
          }
        )
        // this.saveTemplate();
        // Clear selection if the deleted folder was selected
        if (this.selectedFolderId === folder.id) {
          this.selectedFolderId = null;
          this.currentForm = null;
          this.fields = [];
        }
      }
    }
  }

  // Send current order to backend immediately
  private sendOrderToBackend(): void {
    if (!this.template.folders) return;

    // Build current state with all folders and their forms
    const folders = this.template.folders.map((folder: any) => ({
      folder_id: folder.id,
      folder_order: folder.folder_order,
      forms: (folder.forms || []).map((form: any) => ({
        form_id: form.id,
        form_order: form.form_order
      }))
    }));

    // Prepare payload
    const payload = {
      template_id: this.templateId,
      folder: folders
    };

    console.log('Sending order to backend:', payload);

    // Call API to update order
    this.apiService.updateBulkOrder(payload).subscribe(
      response => {
        console.log('Order updated successfully:', response);
      },
      error => {
        console.error('Error updating order:', error);
        alert('Failed to save changes. Please try again.');
        // Reload the template to revert to server state
        this.loadDefaultFolders();
      }
    );
  }

  // Helper method to check if form order has changed
  private hasFormOrderChanged(originalForms: any[], currentForms: any[]): boolean {
    if (!originalForms && !currentForms) return false;
    if (!originalForms || !currentForms) return true;
    if (originalForms.length !== currentForms.length) return true;

    // Check if any form has changed position
    for (const currentForm of currentForms) {
      const originalForm = originalForms.find(
        (orig: any) => orig.form_id === currentForm.form_id
      );

      if (!originalForm) {
        // New form
        return true;
      }

      if (originalForm.form_order !== currentForm.form_order) {
        // Form order changed
        return true;
      }
    }

    // Check for deleted forms
    for (const originalForm of originalForms) {
      const existsInCurrent = currentForms.some(
        (current: any) => current.form_id === originalForm.form_id
      );
      if (!existsInCurrent) {
        // Form was deleted
        return true;
      }
    }

    return false;
  }

  // Helper method to get only forms that have changed order

  // Form management methods
  editForm(form: any): void {
    if (form.is_locked) {
      alert('This form is locked. No changes are allowed.');
      return;
    }
    if (form.is_frozen) {
      alert('This form is frozen. Please unfreeze it to make changes.');
      return;
    }
    const newName = prompt('Enter new form name:', form.name);
    if (newName && newName.trim() && newName !== form.name) {
      const trimmedName = newName.trim();
      // Check if form name already exists in the same folder
      const folder = this.template.folders.find((f: any) => 
        f.forms && f.forms.some((formItem: any) => formItem.name === trimmedName && formItem.id !== form.id)
      );
      if (folder) {
        alert('A form with this name already exists in this folder');
        return;
      }
      const oldName = form.name;
      form.name = trimmedName;
      // Update current form if it's the one being edited
      if (this.currentForm && this.currentForm.id === form.id) {
        this.currentForm.name = trimmedName;
        this.selectedForm = trimmedName;
      }

      // Call API to update form
      const folderId = this.getFolderIdByForm(form);
      const updateData = {
        id: folderId,
        form: {
          id: form.id,
          folder_id: folderId,
          name: trimmedName,
          route: form.route,
          form_order: form.form_order
        }
      };
      this.apiService.editForm(this.templateId, updateData).subscribe(
        response => {
          console.log('Form updated successfully:', response);
          const wasCurrentForm = this.currentForm && this.currentForm.id === form.id;
          
          // Update URL with new form name if this is the currently open form
          if (wasCurrentForm) {
            const updatedUrl = `/ecrf-template/${this.templateId}/form/${encodeURIComponent(trimmedName)}`;
            this.router.navigate([updatedUrl], { replaceUrl: true });
          }
          
          // Refetch template data to stay up to date
          // Pass form ID to ensure we stay on the same form after renaming
          if (wasCurrentForm) {
            this.loadTemplate(form.id);
          } else {
            this.loadTemplate();
          }
        },
        error => {
          console.error('Error updating form:', error);
          // Revert the change on error
          form.name = oldName;
          if (this.currentForm && this.currentForm.id === form.id) {
            this.currentForm.name = oldName;
            this.selectedForm = oldName;
          }
          alert(`Failed to update form: ${error.message || 'Unknown error'}`);
        }
      );
    }
  }

  deleteForm(form: any): void {
    if (form.is_locked) {
      alert('This form is locked. No changes are allowed.');
      return;
    }
    if (form.is_frozen) {
      alert('This form is frozen. Please unfreeze it to make changes.');
      return;
    }
    if (confirm(`Are you sure you want to delete the form "${form.name}"? This action cannot be undone.`)) {
      // Find the folder containing this form
      const folder = this.template.folders.find((f: any) => 
        f.forms && f.forms.some((formItem: any) => formItem.id === form.id)
      );
      if (folder) {
        const formIndex = folder.forms.findIndex((f: any) => f.id === form.id);
        if (formIndex > -1) {
          // Call API to delete form
          const deleteData = {
            id: folder.id,
            form: {
              id: form.id
            }
          };
          this.apiService.deleteForm(this.templateId, deleteData).subscribe(
            response => {
              console.log('Form deleted successfully:', response);
              // Remove form from local array
              folder.forms.splice(formIndex, 1);
              // this.saveTemplate();
              // Clear selection if the deleted form was selected
              if (this.currentForm && this.currentForm.id === form.id) {
                this.currentForm = null;
                this.fields = [];
                this.selectedForm = null;
                this.form = new FormGroup({});
              }
            },
            error => {
              console.error('Error deleting form:', error);
              alert(`Failed to delete form: ${error.message || 'Unknown error'}`);
            }
          );
        }
      }
    }
  }








  // Dependency-related methods
  loadAvailableFolders(): void {
    // Load folders from the API using current template data
    if (this.template && this.template.folders) {
      this.availableFolders = this.template.folders.map((folder: any) => ({
        id: folder.id,
        name: folder.name
      }));
    } else {
      // Fallback to API call if template not loaded
      this.apiService.getTemplateData(this.templateId).subscribe(response => {
        if (response.template && response.template.folders) {
          this.availableFolders = response.template.folders.map((folder: any) => ({
            id: folder.id,
            name: folder.name
          }));
        }
      });
    }
  }

  onDependencyToggle(): void {
    if (!this.newField.hasDependency) {
      // Reset dependency configuration
      this.newField.dependency = {
        folderId: '',
        formId: '',
        fieldId: '',
        triggerValue: ''
      };
      this.availableForms = [];
      this.availableFields = [];
      this.selectedDependencyField = null;
    }
  }

  onFolderChange(): void {
    // Reset form and field selections
    this.newField.dependency.formId = '';
    this.newField.dependency.fieldId = '';
    this.newField.dependency.triggerValue = '';
    this.availableForms = [];
    this.availableFields = [];
    this.selectedDependencyField = null;
    if (this.newField.dependency.folderId) {
      // Find the selected folder from template data
      const selectedFolder = this.template?.folders?.find((folder: any) => folder.id === this.newField.dependency.folderId);
      if (selectedFolder && selectedFolder.forms) {
        this.availableForms = selectedFolder.forms.map((form: any) => ({
          id: form.id,
          name: form.name
        }));
      } else {
        // Fallback to API call if folder not found in current template
        this.apiService.getTemplateData(this.templateId).subscribe(response => {
          if (response.template && response.template.folders) {
            const apiSelectedFolder = response.template.folders.find((folder: any) => folder.id === this.newField.dependency.folderId);
            if (apiSelectedFolder && apiSelectedFolder.forms) {
              this.availableForms = apiSelectedFolder.forms.map((form: any) => ({
                id: form.id,
                name: form.name
              }));
            }
          }
        });
      }
    }
  }

  onFormChange(): void {
    // Reset field selection
    this.newField.dependency.fieldId = '';
    this.newField.dependency.triggerValue = '';
    this.availableFields = [];
    this.selectedDependencyField = null;
    if (this.newField.dependency.folderId && this.newField.dependency.formId) {
      // Find the selected folder and form from template data
      const selectedFolder = this.template?.folders?.find((folder: any) => folder.id === this.newField.dependency.folderId);
      if (selectedFolder && selectedFolder.forms) {
        const selectedForm = selectedFolder.forms.find((form: any) => form.id === this.newField.dependency.formId);
        if (selectedForm && selectedForm.fields) {
          console.log('Adding to available Field')
          // Filter to only show Dropdown, Radio, and Checkbox field types
          this.availableFields = selectedForm.fields
            .filter((field: any) => {
              const fieldType = (field.type || '').toLowerCase();
              return fieldType === 'dropdown' || 
                     fieldType === 'radio' || 
                     fieldType === 'radio-button' ||
                     fieldType === 'checkbox' ||
                     fieldType === 'checkbox-group' ||
                     fieldType === 'select';
            })
            .map((field: any) => ({
              id: field.id,
              title: field.title,
              type: field.type,
              options: field.options || [],
              originalField: field // Store original field for reference
            }));
        }
      } else {
        // Fallback to API call if folder/form not found in current template
        console.log("newField: ", this.newField.dependency);
        this.apiService.getTemplateData(this.templateId).subscribe(response => {
          if (response.template && response.template.folders) {
            const apiSelectedFolder = response.template.folders.find((folder: any) => folder.id === this.newField.dependency.folderId);
            if (apiSelectedFolder && apiSelectedFolder.forms) {
              const apiSelectedForm = apiSelectedFolder.forms.find((form: any) => form.id === this.newField.dependency.formId);
              if (apiSelectedForm && apiSelectedForm.fields) {
                // Filter to only show Dropdown, Radio, and Checkbox field types
                this.availableFields = apiSelectedForm.fields
                  .filter((field: any) => {
                    const fieldType = (field.type || '').toLowerCase();
                    return fieldType === 'dropdown' || 
                           fieldType === 'radio' || 
                           fieldType === 'radio-button' ||
                           fieldType === 'checkbox' ||
                           fieldType === 'checkbox-group' ||
                           fieldType === 'select';
                  })
                  .map((field: any) => ({
                    id: field.id,
                    title: field.title,
                    type: field.type,
                    options: field.options || [],
                    originalField: field // Store original field for reference
                  }));
              }
            }
          }
        });
      }
    }
  }

  onFieldChange(): void {
    // Reset trigger value
    this.newField.dependency.triggerValue = '';
    this.selectedDependencyField = null;
    if (this.newField.dependency.fieldId) {
      // Find the selected field
      console.log("[onFieldChange] Avaiables Fields:", this.availableFields)
      console.log("[onFieldChange] DependencyFieldId: ", this.newField.dependency.fieldId);
      this.selectedDependencyField = this.availableFields.find(field => {
        console.log(field.id, this.newField.dependency.fieldId);
        if (field.id == this.newField.dependency.fieldId) {
          return field
        }
      });
      // Debug logging
      console.log('Selected dependency field:', this.selectedDependencyField);
      console.log('Available options:', this.selectedDependencyField?.options);
    }
  }

  /** Returns a fresh validation config (supports multiple validation types). */
  private getEmptyValidation(): any {
    return {
      validationTypes: [] as string[],
      fieldSelection: '',
      dependency: {
        folderId: '',
        formId: '',
        fieldId: '',
        triggerValue: ''
      },
      rangeLimitCheck: { minValue: null, maxValue: null, queryMessage: '' },
      missingDataQueries: { startDate: '', endDate: '', buffer: null, queryMessage: '' },
      validationType: '',
      minValue: null,
      maxValue: null,
      queryMessage: '',
      startDate: '',
      endDate: '',
      buffer: null
    };
  }

  /** Normalize legacy single-type validation into validationTypes + rangeLimitCheck/missingDataQueries. */
  private normalizeValidationShape(v: any): void {
    if (!v) return;
    if (!Array.isArray(v.validationTypes)) v.validationTypes = [];
    if (!v.rangeLimitCheck) v.rangeLimitCheck = { minValue: null, maxValue: null, queryMessage: '' };
    if (!v.missingDataQueries) v.missingDataQueries = { startDate: '', endDate: '', buffer: null, queryMessage: '' };
    if (v.validationType && typeof v.validationType === 'string' && !v.validationTypes.includes(v.validationType)) {
      v.validationTypes.push(v.validationType);
      if (v.validationType === 'range-limit-check') {
        v.rangeLimitCheck.minValue = v.minValue ?? v.rangeLimitCheck.minValue;
        v.rangeLimitCheck.maxValue = v.maxValue ?? v.rangeLimitCheck.maxValue;
        v.rangeLimitCheck.queryMessage = (v.queryMessage != null && v.queryMessage !== '') ? v.queryMessage : v.rangeLimitCheck.queryMessage;
      }
      if (v.validationType === 'missing-data-queries') {
        v.missingDataQueries.startDate = v.startDate ?? v.missingDataQueries.startDate;
        v.missingDataQueries.endDate = v.endDate ?? v.missingDataQueries.endDate;
        v.missingDataQueries.buffer = v.buffer ?? v.missingDataQueries.buffer;
        v.missingDataQueries.queryMessage = (v.queryMessage != null && v.queryMessage !== '') ? v.queryMessage : v.missingDataQueries.queryMessage;
      }
    }
  }

  hasValidationType(type: string): boolean {
    const v = this.newField?.validation;
    if (!v) return false;
    const types = Array.isArray(v.validationTypes) ? v.validationTypes : [];
    return types.includes(type);
  }

  /** Return validation payload for API; syncs legacy validationType and top-level fields for backward compat. */
  getValidationForApi(): any {
    const v = this.newField?.validation;
    if (!v || !this.newField.hasValidation) return null;
    const types = Array.isArray(v.validationTypes) ? v.validationTypes : [];
    if (types.length === 0) return null;
    const out = { ...v };
    if (v.rangeLimitCheck) out.rangeLimitCheck = { ...v.rangeLimitCheck };
    if (v.missingDataQueries) out.missingDataQueries = { ...v.missingDataQueries };
    out.validationType = types[0] || v.validationType || '';
    if (types.includes('range-limit-check') && v.rangeLimitCheck) {
      out.minValue = v.rangeLimitCheck.minValue;
      out.maxValue = v.rangeLimitCheck.maxValue;
      out.queryMessage = v.rangeLimitCheck.queryMessage;
    }
    if (types.includes('missing-data-queries') && v.missingDataQueries) {
      out.startDate = v.missingDataQueries.startDate;
      out.endDate = v.missingDataQueries.endDate;
      out.buffer = v.missingDataQueries.buffer;
      if (types.length === 1) out.queryMessage = v.missingDataQueries.queryMessage;
    }
    return out;
  }

  toggleValidationType(type: string): void {
    const v = this.newField?.validation;
    if (!v) return;
    if (!Array.isArray(v.validationTypes)) v.validationTypes = [];
    const idx = v.validationTypes.indexOf(type);
    if (idx >= 0) {
      v.validationTypes.splice(idx, 1);
      if (type === 'range-limit-check') {
        v.rangeLimitCheck = { minValue: null, maxValue: null, queryMessage: '' };
        v.fieldSelection = '';
        v.dependency = { folderId: '', formId: '', fieldId: '', triggerValue: '' };
        this.availableValidationForms = [];
        this.availableValidationFields = [];
        this.selectedValidationField = null;
      }
      if (type === 'missing-data-queries') {
        v.missingDataQueries = { startDate: '', endDate: '', buffer: null, queryMessage: '' };
      }
    } else {
      v.validationTypes.push(type);
    }
    this.normalizeValidationShape(v);
  }

  // Validation-related methods
  onValidationToggle(): void {
    if (!this.newField.hasValidation) {
      this.newField.validation = this.getEmptyValidation();
      this.availableValidationForms = [];
      this.availableValidationFields = [];
      this.selectedValidationField = null;
    }
  }

  onValidationTypeChange(): void {
    // No-op when using multi-select; kept for any legacy bindings
  }

  onValidationFieldSelectionChange(): void {
    // Reset dependent field configuration when field selection changes
    if (this.newField.validation.fieldSelection === 'this-field') {
      this.newField.validation.dependency = {
        folderId: '',
        formId: '',
        fieldId: '',
        triggerValue: ''
      };
      this.availableValidationForms = [];
      this.availableValidationFields = [];
      this.selectedValidationField = null;
    }
  }

  onValidationFolderChange(): void {
    // Reset form and field selections
    this.newField.validation.dependency.formId = '';
    this.newField.validation.dependency.fieldId = '';
    this.newField.validation.dependency.triggerValue = '';
    this.availableValidationForms = [];
    this.availableValidationFields = [];
    this.selectedValidationField = null;
    if (this.newField.validation.dependency.folderId) {
      // Find the selected folder from template data
      const selectedFolder = this.template?.folders?.find((folder: any) => folder.id === this.newField.validation.dependency.folderId);
      if (selectedFolder && selectedFolder.forms) {
        this.availableValidationForms = selectedFolder.forms.map((form: any) => ({
          id: form.id,
          name: form.name
        }));
      } else {
        // Fallback to API call if folder not found in current template
        this.apiService.getTemplateData(this.templateId).subscribe(response => {
          if (response.template && response.template.folders) {
            const apiSelectedFolder = response.template.folders.find((folder: any) => folder.id === this.newField.validation.dependency.folderId);
            if (apiSelectedFolder && apiSelectedFolder.forms) {
              this.availableValidationForms = apiSelectedFolder.forms.map((form: any) => ({
                id: form.id,
                name: form.name
              }));
            }
          }
        });
      }
    }
  }

  onValidationFormChange(): void {
    // Reset field selection
    this.newField.validation.dependency.fieldId = '';
    this.newField.validation.dependency.triggerValue = '';
    this.availableValidationFields = [];
    this.selectedValidationField = null;
    if (this.newField.validation.dependency.folderId && this.newField.validation.dependency.formId) {
      // Find the selected folder and form from template data
      const selectedFolder = this.template?.folders?.find((folder: any) => folder.id === this.newField.validation.dependency.folderId);
      if (selectedFolder && selectedFolder.forms) {
        const selectedForm = selectedFolder.forms.find((form: any) => form.id === this.newField.validation.dependency.formId);
        if (selectedForm && selectedForm.fields) {
          // Show all usable fields for validation (match dependency-like behavior)
          // Exclude display-only / unsupported field types.
          this.availableValidationFields = selectedForm.fields
            .filter((field: any) => {
              const fieldType = (field.type || '').toLowerCase();
              return !['title', 'instruction', 'esignature', 'signature'].includes(fieldType);
            })
            .map((field: any) => {
              const fieldType = (field.type || '').toLowerCase();
              // Only include options for field types that actually use options
              const optionFieldTypes = ['radio', 'radio-button', 'dropdown', 'select', 'checkbox', 'checkbox-group'];
              const shouldHaveOptions = optionFieldTypes.includes(fieldType);
              return {
                id: field.id,
                name: field.name || field.title?.toLowerCase().replace(/\s+/g, '_'),
                title: field.title,
                type: field.type,
                options: shouldHaveOptions ? (field.options || []) : [],
                originalField: field // Store original field for reference
              };
            });
        }
      } else {
        // Fallback to API call if folder/form not found in current template
        this.apiService.getTemplateData(this.templateId).subscribe(response => {
          if (response.template && response.template.folders) {
            const apiSelectedFolder = response.template.folders.find((folder: any) => folder.id === this.newField.validation.dependency.folderId);
            if (apiSelectedFolder && apiSelectedFolder.forms) {
              const apiSelectedForm = apiSelectedFolder.forms.find((form: any) => form.id === this.newField.validation.dependency.formId);
              if (apiSelectedForm && apiSelectedForm.fields) {
                // Show all usable fields for validation (match dependency-like behavior)
                // Exclude display-only / unsupported field types.
                this.availableValidationFields = apiSelectedForm.fields
                  .filter((field: any) => {
                    const fieldType = (field.type || '').toLowerCase();
                    return !['title', 'instruction', 'esignature', 'signature'].includes(fieldType);
                  })
                  .map((field: any) => {
                    const fieldType = (field.type || '').toLowerCase();
                    // Only include options for field types that actually use options
                    const optionFieldTypes = ['radio', 'radio-button', 'dropdown', 'select', 'checkbox', 'checkbox-group'];
                    const shouldHaveOptions = optionFieldTypes.includes(fieldType);
                    return {
                      id: field.id,
                      name: field.name || field.title?.toLowerCase().replace(/\s+/g, '_'),
                      title: field.title,
                      type: field.type,
                      options: shouldHaveOptions ? (field.options || []) : [],
                      originalField: field // Store original field for reference
                    };
                  });
              }
            }
          }
        });
      }
    }
  }

  onValidationFieldChange(): void {
    this.selectedValidationField = null;
    this.newField.validation.dependency.triggerValue = '';
    if (this.newField.validation.dependency.fieldId) {
      // Find the selected field - try by ID first, then by name/title as fallback
      this.selectedValidationField = this.availableValidationFields.find(field => {
        // Try matching by ID first
        if (field.id != null && field.id == this.newField.validation.dependency.fieldId) {
          return true;
        }
        // Fallback: if fieldId is actually a name (for fields without IDs), match by name
        if (!field.id && field.name && field.name === this.newField.validation.dependency.fieldId) {
          return true;
        }
        // Another fallback: match by title if name matches
        if (!field.id && field.title && field.title.toLowerCase().replace(/\s+/g, '_') === this.newField.validation.dependency.fieldId) {
          return true;
        }
        return false;
      });
      
      // If still not found and fieldId looks like a name, try direct name match
      if (!this.selectedValidationField) {
        this.selectedValidationField = this.availableValidationFields.find(field => 
          field.name === this.newField.validation.dependency.fieldId || 
          field.title === this.newField.validation.dependency.fieldId
        );
      }
      
      console.log('Selected validation field:', this.selectedValidationField);
      console.log('Available validation fields:', this.availableValidationFields);
      console.log('Looking for field ID:', this.newField.validation.dependency.fieldId);
      if (this.selectedValidationField) {
        console.log('Selected field options:', this.selectedValidationField.options);
        console.log('Selected field type:', this.selectedValidationField.type);
        
        // For number fields, clear triggerValue as it's not needed
        const fieldType = (this.selectedValidationField.type || '').toLowerCase();
        if (fieldType === 'number') {
          this.newField.validation.dependency.triggerValue = '';
        }
      } else {
        console.warn('Could not find validation field with ID:', this.newField.validation.dependency.fieldId);
      }
    }
  }

  // Get field name for form controls
  getFieldName(field: any): string {
    return field.name || field.title?.toLowerCase().replace(/\s+/g, '_') || 'field_' + Date.now();
  }

  // Method to check if a field should be visible based on its dependency
  shouldShowField(field: any): boolean {
    // In build mode, always show all fields
    if (this.isBuildMode) {
      return true;
    }
    if (!field.hasDependency || !field.dependency) {
      return true; // Show field if no dependency
    }
    const dependency = field.dependency;
    // Get the dependency field name - try multiple approaches
    let dependencyFieldName = field.dependencyFieldName || this.getDependencyFieldName(dependency);
    // If still no field name, try to find it in the current form fields
    if (!dependencyFieldName) {
      // Look for a field with the same title as the dependency field
      const dependencyField = this.fields.find(f => 
        f.name === field.dependencyFieldName
      );
    }
    if (!dependencyFieldName) {
      console.log(`No dependency field name found for field "${field.title}"`);
      return true; // Show field if dependency field not found
    }

    // Check if this is a cross-form dependency
    const crossFormKey = `${dependency.folderId}_${dependency.formId}_${dependencyFieldName}`;
    let dependencyValue = null;
    if (this.formValuesCache[crossFormKey] !== undefined) {
      // This is a cross-form dependency
      dependencyValue = this.formValuesCache[crossFormKey];
      console.log(`Cross-form dependency field "${dependencyFieldName}" value from cache:`, dependencyValue);
    } else {
      // This is a same-form dependency
      const dependencyControl = this.form.get(dependencyFieldName);
      if (dependencyControl) {
        dependencyValue = dependencyControl.value;
        console.log(`Same-form dependency field "${dependencyFieldName}" value:`, dependencyValue);
      } else {
        console.log(`Dependency field "${dependencyFieldName}" not found in current form`);
        return true; // Show field if dependency field not found
      }
    }
    console.log(`Dependency field "${dependencyFieldName}" value:`, dependencyValue, 'Trigger value:', dependency.triggerValue);
    // Check if the dependency condition is met
    if (dependency.triggerValue === '' || dependency.triggerValue === null || dependency.triggerValue === undefined) {
      return true; // Show field if no trigger value specified
    }

    // Compare values based on field type
    let shouldShow = false;
    const triggerValue: any = dependency.triggerValue;
    // Handle different data types
    if (typeof dependencyValue === 'boolean') {
      shouldShow = dependencyValue === (triggerValue === 'true' || triggerValue === true);
    } else if (Array.isArray(dependencyValue)) {
      shouldShow = (dependencyValue as any[]).includes(triggerValue);
    } else if (typeof dependencyValue === 'string') {
      // Handle string comparison (case-insensitive for better UX)
      const triggerStr = String(triggerValue || '');
      shouldShow = (dependencyValue as any).toLowerCase() === (triggerStr as any).toLowerCase();
    } else if (typeof dependencyValue === 'number') {
      shouldShow = dependencyValue === parseFloat(String(triggerValue));
    } else {
      shouldShow = dependencyValue === triggerValue;
    }
    console.log(`Field "${field.title}" should show:`, shouldShow);
    return shouldShow;
  }

  // Get all fields that depend on a given field
  getDependentFields(field: any): any[] {
    if (!field || !field.name) {
      return [];
    }

    // Find all fields that have this field as a dependency
    return this.fields.filter(f => {
      if (!f.hasDependency || !f.dependency) {
        return false;
      }

      // Check if this field is the dependency field
      const dependencyFieldName = this.getDependencyFieldName(f.dependency);
      return dependencyFieldName === field.name;
    });
  }

  // Populate validation dropdowns when editing a field with validation
  private populateValidationDropdowns(): void {
    if (!this.newField.validation || !this.newField.validation.dependency || !this.newField.validation.dependency.folderId) return;
    // Find the selected folder from template data
    const selectedFolder = this.template?.folders?.find((folder: any) => folder.id === this.newField.validation.dependency.folderId);
    if (selectedFolder && selectedFolder.forms) {
      this.availableValidationForms = selectedFolder.forms.map((form: any) => ({
        id: form.id,
        name: form.name
      }));
      // If form is selected, load fields
      if (this.newField.validation.dependency.formId) {
        const selectedForm = selectedFolder.forms.find((form: any) => form.id === this.newField.validation.dependency.formId);
        if (selectedForm && selectedForm.fields) {
          // Show all usable fields for validation (match dependency-like behavior)
          // Exclude display-only / unsupported field types.
          this.availableValidationFields = selectedForm.fields
            .filter((field: any) => {
              const fieldType = (field.type || '').toLowerCase();
              return !['title', 'instruction', 'esignature', 'signature'].includes(fieldType);
            })
            .map((field: any) => {
              const fieldType = (field.type || '').toLowerCase();
              // Only include options for field types that actually use options
              const optionFieldTypes = ['radio', 'radio-button', 'dropdown', 'select', 'checkbox', 'checkbox-group'];
              const shouldHaveOptions = optionFieldTypes.includes(fieldType);
              return {
                id: field.id,
                name: field.name || field.title?.toLowerCase().replace(/\s+/g, '_'),
                title: field.title,
                type: field.type,
                options: shouldHaveOptions ? (field.options || []) : [],
                originalField: field // Store original field for reference
              };
            });
          // If field is selected, set the selected validation field
          if (this.newField.validation.dependency.fieldId) {
            // Try matching by ID first, then by name/title as fallback
            this.selectedValidationField = this.availableValidationFields.find(field => {
              // Try matching by ID first
              if (field.id != null && field.id == this.newField.validation.dependency.fieldId) {
                return true;
              }
              // Fallback: if fieldId is actually a name (for fields without IDs), match by name
              if (!field.id && field.name && field.name === this.newField.validation.dependency.fieldId) {
                return true;
              }
              // Another fallback: match by title if name matches
              if (!field.id && field.title && field.title.toLowerCase().replace(/\s+/g, '_') === this.newField.validation.dependency.fieldId) {
                return true;
              }
              return false;
            });
            
            // If still not found and fieldId looks like a name, try direct name match
            if (!this.selectedValidationField) {
              this.selectedValidationField = this.availableValidationFields.find(field => 
                field.name === this.newField.validation.dependency.fieldId || 
                field.title === this.newField.validation.dependency.fieldId
              );
            }
          }
        }
      }
    } else {
      // Fallback to API call if folder not found in current template
      this.apiService.getTemplateData(this.templateId).subscribe(response => {
        if (response.template && response.template.folders) {
          const apiSelectedFolder = response.template.folders.find((folder: any) => folder.id === this.newField.validation.dependency.folderId);
          if (apiSelectedFolder && apiSelectedFolder.forms) {
            this.availableValidationForms = apiSelectedFolder.forms.map((form: any) => ({
              id: form.id,
              name: form.name
            }));
            // If form is selected, load fields
            if (this.newField.validation.dependency.formId) {
              const apiSelectedForm = apiSelectedFolder.forms.find((form: any) => form.id === this.newField.validation.dependency.formId);
              if (apiSelectedForm && apiSelectedForm.fields) {
                // Show all usable fields for validation (match dependency-like behavior)
                // Exclude display-only / unsupported field types.
                this.availableValidationFields = apiSelectedForm.fields
                  .filter((field: any) => {
                    const fieldType = (field.type || '').toLowerCase();
                    return !['title', 'instruction', 'esignature', 'signature'].includes(fieldType);
                  })
                  .map((field: any) => {
                    const fieldType = (field.type || '').toLowerCase();
                    // Only include options for field types that actually use options
                    const optionFieldTypes = ['radio', 'radio-button', 'dropdown', 'select', 'checkbox', 'checkbox-group'];
                    const shouldHaveOptions = optionFieldTypes.includes(fieldType);
                    return {
                      id: field.id,
                      name: field.name || field.title?.toLowerCase().replace(/\s+/g, '_'),
                      title: field.title,
                      type: field.type,
                      options: shouldHaveOptions ? (field.options || []) : [],
                      originalField: field // Store original field for reference
                    };
                  });
                // If field is selected, set the selected validation field
                if (this.newField.validation.dependency.fieldId) {
                  // Try matching by ID first, then by name/title as fallback
                  this.selectedValidationField = this.availableValidationFields.find(field => {
                    // Try matching by ID first
                    if (field.id != null && field.id == this.newField.validation.dependency.fieldId) {
                      return true;
                    }
                    // Fallback: if fieldId is actually a name (for fields without IDs), match by name
                    if (!field.id && field.name && field.name === this.newField.validation.dependency.fieldId) {
                      return true;
                    }
                    // Another fallback: match by title if name matches
                    if (!field.id && field.title && field.title.toLowerCase().replace(/\s+/g, '_') === this.newField.validation.dependency.fieldId) {
                      return true;
                    }
                    return false;
                  });
                  
                  // If still not found and fieldId looks like a name, try direct name match
                  if (!this.selectedValidationField) {
                    this.selectedValidationField = this.availableValidationFields.find(field => 
                      field.name === this.newField.validation.dependency.fieldId || 
                      field.title === this.newField.validation.dependency.fieldId
                    );
                  }
                }
              }
            }
          }
        }
      });
    }
  }

  // Populate dependency dropdowns when editing a field with dependencies
  private populateDependencyDropdowns(): void {
    if (!this.newField.dependency.folderId) return;
    // Find the selected folder from template data
    const selectedFolder = this.template?.folders?.find((folder: any) => folder.id === this.newField.dependency.folderId);
    if (selectedFolder && selectedFolder.forms) {
      this.availableForms = selectedFolder.forms.map((form: any) => ({
        id: form.id,
        name: form.name
      }));
      // If form is selected, load fields
      if (this.newField.dependency.formId) {
        const selectedForm = selectedFolder.forms.find((form: any) => form.id === this.newField.dependency.formId);
        if (selectedForm && selectedForm.fields) {
          // Filter to only show Dropdown, Radio, and Checkbox field types
          this.availableFields = selectedForm.fields
            .filter((field: any) => {
              const fieldType = (field.type || '').toLowerCase();
              return fieldType === 'dropdown' || 
                     fieldType === 'radio' || 
                     fieldType === 'radio-button' ||
                     fieldType === 'checkbox' ||
                     fieldType === 'checkbox-group' ||
                     fieldType === 'select';
            })
            .map((field: any) => ({
              id: field.id,
              title: field.title,
              type: field.type,
              options: field.options || [],
              originalField: field // Store original field for reference
            }));
          // If field is selected, set the selected dependency field
          if (this.newField.dependency.fieldId) {
            console.log("Available Fields: ", this.availableFields);
            this.selectedDependencyField = this.availableFields.find(field => 
              field.id === this.newField.dependency.fieldId
            );
          }
        }
      }
    } else {
      // Fallback to API call if folder not found in current template
      this.apiService.getTemplateData(this.templateId).subscribe(response => {
        if (response.template && response.template.folders) {
          const apiSelectedFolder = response.template.folders.find((folder: any) => folder.id === this.newField.dependency.folderId);
          if (apiSelectedFolder && apiSelectedFolder.forms) {
            this.availableForms = apiSelectedFolder.forms.map((form: any) => ({
              id: form.id,
              name: form.name
            }));
            // If form is selected, load fields
            if (this.newField.dependency.formId) {
              const apiSelectedForm = apiSelectedFolder.forms.find((form: any) => form.id === this.newField.dependency.formId);
              if (apiSelectedForm && apiSelectedForm.fields) {
                // Filter to only show Dropdown, Radio, and Checkbox field types
                this.availableFields = apiSelectedForm.fields
                  .filter((field: any) => {
                    const fieldType = (field.type || '').toLowerCase();
                    return fieldType === 'dropdown' || 
                           fieldType === 'radio' || 
                           fieldType === 'radio-button' ||
                           fieldType === 'checkbox' ||
                           fieldType === 'checkbox-group' ||
                           fieldType === 'select';
                  })
                  .map((field: any) => ({
                    id: field.id,
                    title: field.title,
                    type: field.type,
                    options: field.options || [],
                    originalField: field // Store original field for reference
                  }));
                // If field is selected, set the selected dependency field
                if (this.newField.dependency.fieldId) {
                  this.selectedDependencyField = this.availableFields.find(field => 
                    field.id === this.newField.dependency.fieldId
                  );
                }
              }
            }
          }
        }
      });
    }
  }

  // Set up dependency field listeners for all fields with dependencies
  private setupDependencyFieldListeners(): void {
    this.fields.forEach(field => {
      if (field.hasDependency && field.dependencyFieldName) {
        const dependencyControl = this.form.get(field.dependencyFieldName);
        if (dependencyControl) {
          dependencyControl.valueChanges.subscribe(value => {
            console.log(`Dependency field "${field.dependencyFieldName}" changed to:`, value);
            this.cdr.detectChanges();
          });
        } else {
          console.warn(`Dependency field "${field.dependencyFieldName}" not found in form for field "${field.title}"`);
        }
      }
    });
  }

  // Set up listeners for dependency fields that exist in the current form
  private setupCurrentFormDependencyListeners(): void {
    // Check if any current form fields are dependency fields for other fields
    this.fields.forEach(field => {
      if (this.form.get(field.name)) {
        this.form.get(field.name)?.valueChanges.subscribe(value => {
          // Check if this field is a dependency for any other fields
          const dependentFields = this.fields.filter(f => 
            f.hasDependency && 
            f.dependencyFieldName === field.name
          );
          if (dependentFields.length > 0) {
            console.log(`Field "${field.title}" is a dependency for ${dependentFields.length} fields`);
            this.cdr.detectChanges();
          }
        });
      }
    });
  }

  // Check for cross-form dependencies when a field value changes
  private checkCrossFormDependencies(fieldName: string, value: any): void {
    const currentFormKey = `${this.selectedFolderId}_${this.currentForm?.name}_${fieldName}`;
    // Check if this field is a dependency for fields in other forms
    if (this.globalDependencies[currentFormKey]) {
      const dependentFields = this.globalDependencies[currentFormKey];
      console.log(`Field "${fieldName}" is a cross-form dependency for:`, dependentFields);
      // Update the form values cache
      this.formValuesCache[currentFormKey] = value;
      // Trigger change detection for all dependent forms
      dependentFields.forEach(dependentField => {
        console.log(`Triggering dependency update for field "${dependentField.fieldTitle}" in form "${dependentField.currentForm}"`);
        // Check if the dependent field should be visible
        const shouldShow = this.shouldShowCrossFormField(dependentField, value);
        console.log(`Field "${dependentField.fieldTitle}" should show:`, shouldShow);
      });
      // Trigger change detection
      this.cdr.detectChanges();
    }
  }

  // Check if a cross-form dependent field should be visible
  private shouldShowCrossFormField(dependentField: any, dependencyValue: any): boolean {
    // In build mode, always show all fields
    if (this.isBuildMode) {
      return true;
    }
    if (!dependentField.triggerValue) {
      return true;
    }

    // Compare values based on field type
    if (typeof dependencyValue === 'boolean') {
      return dependencyValue === (dependentField.triggerValue === 'true' || dependentField.triggerValue === true);
    } else if (Array.isArray(dependencyValue)) {
      return (dependencyValue as any[]).includes(dependentField.triggerValue);
    } else {
      return dependencyValue === dependentField.triggerValue;
    }
  }

  // Populate form with existing data if available
  private populateFormWithExistingData(form: any): void {
    // Check if form has existing data
    if (form.data && typeof form.data === 'object') {
      console.log('Populating form with existing data from form object:', form.data);
      this.setFormData(form.data);
    } else {
      console.log('No existing data in form object, attempting to load from API');
      // this.loadFormDataFromAPI(form);
    }
  }

  // Set form data from a data object
  private setFormData(data: any): void {
    // Populate form controls with existing data
    Object.keys(data).forEach(key => {
      const formControl = this.form.get(key);
      if (formControl) {
        const value = data[key];
        // Handle different field types
        if (Array.isArray(value)) {
          // For checkbox fields or array values
          formControl.setValue(value);
        } else if (value !== null && value !== undefined) {
          // For other field types
          formControl.setValue(value);
        }
        console.log(`Set form control "${key}" to:`, value);
      }
    });
    // Handle special field types that might have different naming conventions
    this.fields.forEach(field => {
      if (field.type === 'table') {
        // Table functionality will be recreated from scratch
        console.log('Table field detected - will be recreated:', field.name);
      }
    });
    console.log('Form populated with data successfully');
  }

  // Load form data from API
  private loadFormDataFromAPI(form: any): void {
    // Get the required parameters for the API call
    const studyId = this.templateId; // Assuming templateId is the studyId
    const subjectId = 'default'; // You may need to get this from somewhere else
    const folderName = this.selectedFolder || 'default';
    const formName = form.name;
    console.log('Loading form data from API:', { studyId, subjectId, folderName, formName });
    // Call the API to get form data
    this.apiService.getFormData(studyId, subjectId, folderName, formName).subscribe(
      (response) => {
        console.log('Form data loaded from API:', response);
        if (response && response.data) {
          // Populate form with the loaded data
          this.setFormData(response.data);
        } else {
          console.log('No form data found in API response');
        }
      },
      (error) => {
        console.error('Error loading form data from API:', error);
        // Continue with empty form if data loading fails
      }
    );
  }

  // Setup dependencies when opening a form
  private setupFormDependencies(form: any): void {
    console.log('Setting up dependencies for form:', form.name);
    if (!form.fields || form.fields.length === 0) {
      console.log('No fields found in form, skipping dependency setup');
      return;
    }

    // Process each field in the current form
    form.fields.forEach((field: any, fieldIndex: number) => {
      if (field.hasDependency && field.dependency) {
        console.log(`Processing dependency for field "${field.title}"`);
        // Ensure dependency object has all required properties
        this.ensureDependencyProperties(field, fieldIndex);
        // Resolve dependency field name if not already set
        if (!field.dependencyFieldName) {
          field.dependencyFieldName = this.resolveDependencyFieldName(field.dependency);
        }

        // Update dependency registries
        this.updateDependencyRegistries(field, fieldIndex);
        // Setup dependency field listener
        this.setupDependencyFieldListener(field);
      }
    });
    console.log('Form dependencies setup completed');
  }

  // Ensure dependency object has all required properties
  private ensureDependencyProperties(field: any, fieldIndex: number): void {
    const dependency = field.dependency;
    // Set folderId if not present (assume current folder)
    if (!dependency.folderId) {
      dependency.folderId = this.selectedFolderId || '0';
    }

    // Set formId if not present (assume current form)
    if (!dependency.formId) {
      dependency.formId = this.currentForm?.id || '0';
    }

    // Ensure other required properties exist
    if (!dependency.fieldId) {
      console.warn(`Field "${field.title}" has dependency but no fieldId specified`);
    }
    if (!dependency.triggerValue) {
      console.warn(`Field "${field.title}" has dependency but no triggerValue specified`);
    }
  }

  // Resolve dependency field name from dependency information
  private resolveDependencyFieldName(dependency: any): string | null {
    if (!dependency.fieldId) {
      return null;
    }
    try {
      // If we have the template structure, use it to resolve the field name
      if (this.template && this.template.folders) {
        const folderIndex = parseInt(dependency.folderId) || 0;
        const formIndex = parseInt(dependency.formId) || 0;
        const fieldIndex = parseInt(dependency.fieldId);
        const dependencyFolder = this.template.folders[folderIndex];
        if (dependencyFolder && dependencyFolder.forms) {
          const dependencyForm = dependencyFolder.forms[formIndex];
          if (dependencyForm && dependencyForm.fields) {
            const dependencyField = dependencyForm.fields[fieldIndex];
            if (dependencyField) {
              return dependencyField.name || dependencyField.title?.toLowerCase().replace(/\s+/g, '_');
            }
          }
        }
      }

      // Fallback: try to use the fieldTitle or fieldName from dependency
      if (dependency.fieldTitle) {
        return dependency.fieldTitle.toLowerCase().replace(/\s+/g, '_');
      }
      if (dependency.fieldName) {
        return dependency.fieldName;
      }
      return null;
    } catch (error) {
      console.error('Error resolving dependency field name:', error);
      return null;
    }
  }

  // Update dependency registries
  private updateDependencyRegistries(field: any, fieldIndex: number): void {
    if (!field.dependencyFieldName) {
      return;
    }
    const dependency = field.dependency;
    // Update dependency field registry
    const dependencyKey = `${dependency.folderId}_${dependency.formId}_${dependency.fieldId}`;
    this.dependencyFieldRegistry[dependencyKey] = {
      fieldName: field.dependencyFieldName,
      fieldTitle: dependency.fieldTitle || field.dependencyFieldName,
      fieldType: dependency.fieldType,
      options: dependency.options || []
    };
    // Register global dependency
    const globalDependencyKey = `${dependency.folderId}_${dependency.formId}_${field.dependencyFieldName}`;
    if (!this.globalDependencies[globalDependencyKey]) {
      this.globalDependencies[globalDependencyKey] = [];
    }

    // Check if this dependency is already registered
    const existingDependency = this.globalDependencies[globalDependencyKey].find(
      dep => dep.fieldName === field.name
    );
    if (!existingDependency) {
      this.globalDependencies[globalDependencyKey].push({
        folderId: this.selectedFolderId || '0',
        formId: this.currentForm?.id || '0',
        fieldName: field.name,
        fieldTitle: field.title,
        triggerValue: dependency.triggerValue
      });
    }
    console.log(`Updated dependency registries for field "${field.title}":`, {
      dependencyFieldName: field.dependencyFieldName,
      triggerValue: dependency.triggerValue
    });
  }

  // Setup dependency field listener for a specific field
  private setupDependencyFieldListener(field: any): void {
    if (!field.dependencyFieldName) {
      return;
    }
    const dependencyControl = this.form.get(field.dependencyFieldName);
    if (dependencyControl) {
      // Subscribe to value changes of the dependency field
      dependencyControl.valueChanges.subscribe(value => {
        console.log(`Dependency field "${field.dependencyFieldName}" changed to:`, value);
        // Trigger change detection to update dependent field visibility
        this.cdr.detectChanges();
      });
      console.log(`Set up listener for dependency field "${field.dependencyFieldName}"`);
    } else {
      console.warn(`Dependency field "${field.dependencyFieldName}" not found in form controls`);
    }
  }

  // Initialize form values cache for cross-form dependencies
  private initializeFormValuesCache(): void {
    // Cache current form field values
    this.fields.forEach(field => {
      const formKey = `${this.selectedFolderId}_${this.currentForm?.name}_${field.name}`;
      const fieldValue = this.form.get(field.name)?.value;
      this.formValuesCache[formKey] = fieldValue;
    });
    console.log('Initialized form values cache:', this.formValuesCache);
  }

  // Helper method to get the dependency field name from current selection
  private getDependencyFieldNameFromSelection(): string | null {
    console.log("New Field Details for dependency",this.newField);
    if (!this.newField.dependency.fieldId || !this.selectedDependencyField) {
      console.log(this.newField.dependency.fieldId);
      console.log("FieldName set to null");
      return null;
    }

    // Get the field name from the selected dependency field
    const fieldName = this.selectedDependencyField.title.toLowerCase().replace(/\s+/g, '_');
    // Store dependency field information in registry
    const dependencyKey = `${this.newField.dependency.folderId}_${this.newField.dependency.formId}_${this.newField.dependency.fieldId}`;
    this.dependencyFieldRegistry[dependencyKey] = {
      fieldName: fieldName,
      fieldTitle: this.selectedDependencyField.title,
      fieldType: this.selectedDependencyField.type,
      options: this.selectedDependencyField.options
    };
    // Register global dependency
    const globalDependencyKey = `${this.newField.dependency.folderId}_${this.newField.dependency.formId}_${fieldName}`;
    if (!this.globalDependencies[globalDependencyKey]) {
      this.globalDependencies[globalDependencyKey] = [];
    }

    // Add this field as a dependent field
    this.globalDependencies[globalDependencyKey].push({
      currentForm: this.currentForm?.name,
      currentFolder: this.selectedFolderId,
      fieldName: this.newField.title.toLowerCase().replace(/\s+/g, '_'),
      fieldTitle: this.newField.title,
      triggerValue: this.newField.dependency.triggerValue
    });
    console.log('Storing dependency field name:', fieldName, 'for field:', this.selectedDependencyField.title);
    console.log('Global dependencies:', this.globalDependencies);
    console.log("Sending Field Name: ", fieldName);
    return fieldName;
  }

  // Helper method to get folder ID by form
  private getFolderIdByForm(form: any): string | null {
    for (const folder of this.template.folders) {
      if (folder.forms && folder.forms.some((f: any) => f.id === form.id)) {
        return folder.id;
      }
    }
    return null;
  }

  // Helper method to get the dependency field name
  private getDependencyFieldName(dependency: any): string | null {
    if (!dependency.folderId || !dependency.formId || !dependency.fieldId) {
      return null;
    }

    // Try to get from registry first
    const dependencyKey = `${dependency.folderId}_${dependency.formId}_${dependency.fieldId}`;
    const registryEntry = this.dependencyFieldRegistry[dependencyKey];
    if (registryEntry) {
      return registryEntry.fieldName;
    }

    // Find the field in the available fields list (this should be populated when the field was created)
    const field = this.availableFields.find(f => f.id === dependency.fieldId);
    if (field) {
      return field.title.toLowerCase().replace(/\s+/g, '_');
    }

    // Fallback: try to find in current form fields
    const currentField = this.fields.find(f => 
      f.dependency && 
      f.dependency.folderId === dependency.folderId &&
      f.dependency.formId === dependency.formId &&
      f.dependency.fieldId === dependency.fieldId
    );
    if (currentField) {
      return currentField.name;
    }
    return null;
  }

  // Dropdown management methods
  closeAllDropdowns(): void {
    // Close all Bootstrap dropdowns
    const dropdownElements = document.querySelectorAll('.dropdown-menu.show');
    dropdownElements.forEach(dropdown => {
      dropdown.classList.remove('show');
    });
    // Close all custom dropdowns
    const customDropdowns = document.querySelectorAll('.dropdown-menu');
    customDropdowns.forEach(dropdown => {
      dropdown.classList.remove('show');
    });
    // Also close any Bootstrap dropdowns that might be open
    const bootstrapDropdowns = document.querySelectorAll('[data-bs-toggle="dropdown"]');
    bootstrapDropdowns.forEach(dropdown => {
      const dropdownMenu = dropdown.nextElementSibling as HTMLElement;
      if (dropdownMenu && dropdownMenu.classList.contains('dropdown-menu')) {
        dropdownMenu.classList.remove('show');
      }
    });
    // Clear the open dropdowns set
    this.openDropdowns.clear();
    // Close any open modals
    this.isOptionsModalOpen = false;
    this.isModalOpen = false;
    this.isReasonModalOpen = false;
    this.clickedFieldIndex = null;
    this.showReviewModal = false;
    // Force change detection to update the UI
    this.cdr.detectChanges();
  }

  toggleDropdown(dropdownId: string): void {
    if (this.openDropdowns.has(dropdownId)) {
      this.closeDropdown(dropdownId);
    } else {
      this.openDropdown(dropdownId);
    }
  }

  openDropdown(dropdownId: string): void {
    // Close all other dropdowns first
    this.closeAllDropdowns();
    // Open the specified dropdown
    this.openDropdowns.add(dropdownId);
    // Add show class to the dropdown element
    const dropdownElement = document.getElementById(dropdownId);
    if (dropdownElement) {
      dropdownElement.classList.add('show');
    }
  }

  closeDropdown(dropdownId: string): void {
    this.openDropdowns.delete(dropdownId);
    // Remove show class from the dropdown element
    const dropdownElement = document.getElementById(dropdownId);
    if (dropdownElement) {
      dropdownElement.classList.remove('show');
    }
  }

  // Method to handle dropdown button click - closes all other dropdowns before opening
  onDropdownButtonClick(event: Event): void {
    const button = event.target as HTMLElement;
    const dropdownButton = button.closest('[data-bs-toggle="dropdown"]') as HTMLElement;
    
    if (!dropdownButton) return;
    
    // Close all other dropdowns immediately (synchronously) before Bootstrap processes the click
    const clickedParent = dropdownButton.closest('.dropdown');
    const allDropdownMenus = document.querySelectorAll('.dropdown-menu.show');
    
    allDropdownMenus.forEach(menu => {
      // Check if this menu belongs to the clicked button
      const parentDropdown = menu.closest('.dropdown');
      
      // Only close if it's not the dropdown being clicked
      if (parentDropdown !== clickedParent) {
        menu.classList.remove('show');
        // Also remove aria-expanded from the button
        const menuButton = parentDropdown?.querySelector('[data-bs-toggle="dropdown"]') as HTMLElement;
        if (menuButton) {
          menuButton.setAttribute('aria-expanded', 'false');
        }
      }
    });
    
    // Also close any dropdown buttons that have aria-expanded="true" except the clicked one
    const allDropdownButtons = document.querySelectorAll('[data-bs-toggle="dropdown"][aria-expanded="true"]');
    allDropdownButtons.forEach(btn => {
      if (btn !== dropdownButton) {
        btn.setAttribute('aria-expanded', 'false');
        const menu = btn.nextElementSibling as HTMLElement;
        if (menu && menu.classList.contains('dropdown-menu')) {
          menu.classList.remove('show');
        }
      }
    });
  }

  // Enhanced click outside handler
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    
    // Close options modal if clicking outside
    if (this.isOptionsModalOpen) {
      if (!target.closest('.options-modal') && !target.closest('.menu-icon')) {
        this.isOptionsModalOpen = false;
        this.clickedFieldIndex = null;
      }
    }

    // Close dropdowns if clicking outside
    if (!target.closest('.dropdown') && !target.closest('.dropdown-menu')) {
      this.closeAllDropdowns();
    }
  }

  // New table functionality - simple and clean implementation
  // Get table data for a field
  getTableData(fieldName: string): any[] {
    const tableControl = this.form.get(fieldName);
    return tableControl ? tableControl.value || [] : [];
  }

  // Get columns excluding Sr. No for fixed-rows-table
  getTableColumnsExcludingSrNo(field: any): any[] {
    if (field.type === 'fixed-rows-table') {
      return field.columns ? field.columns.filter((col: any) => !col.isSrNo && col.name !== '_sr_no') : [];
    }
    return field.columns || [];
  }

  // Ensure Sr. No column exists in frontend for fixed-rows-table (called when loading fields from backend)
  ensureSrNoColumnForFixedRowsTable(field: any): void {
    if (field.type === 'fixed-rows-table') {
      // Initialize columns array if it doesn't exist
      if (!field.columns) {
        field.columns = [];
      }
      // Remove any existing Sr. No columns first to avoid duplicates
      field.columns = field.columns.filter((col: any) => !col.isSrNo && col.name !== '_sr_no');
      // Add Sr. No column at the beginning (it will always be added since we just removed any existing ones)
      field.columns.unshift({
        title: 'Sr. No',
        type: 'text',
        required: false,
        values: [],
        name: '_sr_no',
        isSrNo: true,
        readOnly: true
      });
    }
  }

  // Get Sr. No column for fixed-rows-table
  getSrNoColumn(field: any): any {
    if (field.type === 'fixed-rows-table' && field.columns) {
      return field.columns.find((col: any) => col.isSrNo || col.name === '_sr_no');
    }
    return null;
  }

  // Get the second column (first non-SrNo column) for fixed-rows-table
  getSecondColumn(field: any): any {
    if (field.type === 'fixed-rows-table' && field.columns) {
      const nonSrNoColumns = field.columns.filter((col: any) => !col.isSrNo && col.name !== '_sr_no');
      return nonSrNoColumns.length > 0 ? nonSrNoColumns[0] : null;
    }
    return null;
  }

  // Get all statement columns for fixed-rows-table
  getStatementColumns(field: any): any[] {
    if (field.type === 'fixed-rows-table' && field.columns) {
      return field.columns.filter((col: any) => 
        this.isStatementColumn(col) && !col.isSrNo && col.name !== '_sr_no'
      );
    }
    return [];
  }

  // Populate table rows from value column for fixed-rows-table fields
  populateFixedRowsTableFromValue(): void {
    if (!this.form || !this.fields) {
      return;
    }

    this.fields.forEach((field: any) => {
      if (field.type === 'fixed-rows-table' && field.columns) {
        const statementColumns = this.getStatementColumns(field);
        
        if (statementColumns.length === 0) {
          return;
        }

        // Check if table already has data - don't overwrite if user has entered data
        const tableControl = this.form.get(field.name);
        if (tableControl && tableControl.value && Array.isArray(tableControl.value) && tableControl.value.length > 0) {
          console.log(`Field ${field.name} already has data, skipping population from value column`);
          return;
        }

        // Find the statement column that has a value (from ecrf_table)
        // The value is stored in the 'value' property of the column object
        // Check all columns (not just statement columns) to find the one with value
        let statementValues: any[] = [];
        
        // First, check all columns to find the one with value
        for (const col of field.columns) {
          if (col.value && col.value !== null && col.value !== '') {
            try {
              // Parse the value if it's a string, otherwise use as is
              const parsedValue = typeof col.value === 'string' ? JSON.parse(col.value) : col.value;
              if (Array.isArray(parsedValue) && parsedValue.length > 0) {
                statementValues = parsedValue;
                console.log(`Found value in column ${col.title} for field ${field.name}:`, statementValues);
                break; // Use the first column that has a valid value array
              }
            } catch (e) {
              console.error(`Error parsing value for field ${field.name}, column ${col.title}:`, e);
            }
          }
        }

        // If we have statement values, reconstruct rows
        if (Array.isArray(statementValues) && statementValues.length > 0) {
          const numStatementColumns = statementColumns.length;
          const numRows = Math.ceil(statementValues.length / numStatementColumns);
          
          // Reconstruct rows: value array is [row1_stmt1, row1_stmt2, ..., row2_stmt1, row2_stmt2, ...]
          const rows: any[] = [];
          
          // Get all columns excluding Sr. No for initializing non-statement columns
          const columnsToUse = this.getTableColumnsExcludingSrNo(field);
          
          for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
            const row: any = {};
            
            // First, initialize all columns (excluding Sr. No) with null
            columnsToUse.forEach((column: any) => {
              const originalIndex = field.columns.indexOf(column);
              const columnKey = column.name || (originalIndex > 0 ? `col_${originalIndex - 1}` : `col_0`);
              // Skip statement columns as we'll set them next
              if (!this.isStatementColumn(column)) {
                row[columnKey] = null;
              }
            });
            
            // Populate each statement column value using the same columnKey logic as when saving
            statementColumns.forEach((col: any, colIndex: number) => {
              const originalIndex = field.columns.indexOf(col);
              const columnKey = col.name || (originalIndex > 0 ? `col_${originalIndex - 1}` : `col_0`);
              const valueIndex = rowIndex * numStatementColumns + colIndex;
              if (valueIndex < statementValues.length) {
                row[columnKey] = statementValues[valueIndex] || '';
              } else {
                row[columnKey] = '';
              }
            });
            
            rows.push(row);
          }

          // Set the table control value
          if (tableControl) {
            tableControl.setValue(rows);
            console.log(`Populated ${rows.length} rows for field ${field.name} from value column`);
            console.log(`Sample row structure:`, rows.length > 0 ? rows[0] : 'No rows');
          }
        } else {
          console.log(`No value found in columns for field ${field.name}`);
        }
      }
    });
  }


  // Get instruction table data (for display-only instruction tables)
  getInstructionTableData(field: any): any[] {
    // Instruction tables are display-only, return empty array or any stored data
    // If the field has predefined rows, return those; otherwise return empty
    return field.instructionTableData || field.predefinedRows || [];
  }

  // Add a new row to the instruction table
  addInstructionTableRow(): void {
    // Use the field we're currently editing (newField)
    const targetField = this.newField;
    
    if (!targetField.instructionTableData) {
      targetField.instructionTableData = [];
    }
    if (!targetField.instructionColumns || targetField.instructionColumns.length === 0) {
      alert('Please add at least one column before adding rows.');
      return;
    }
    // Store reference to the field we're working with
    this.instructionTableRowField = targetField;
    // Initialize empty values for each column using column.title as key
    this.instructionTableRowValues = {};
    targetField.instructionColumns.forEach((column: any) => {
      // Use column.title as the key to ensure correct mapping
      const columnKey = column.title;
      if (columnKey) {
        if (column.type === 'checkbox') {
          this.instructionTableRowValues[columnKey] = false;
        } else {
          this.instructionTableRowValues[columnKey] = '';
        }
      }
    });
    console.log('Initialized instruction row values:', this.instructionTableRowValues);
    console.log('Columns:', targetField.instructionColumns.map((c: any) => c.title));
    // Open modal to collect row values
    this.isInstructionTableRowModalOpen = true;
  }

  // Handle instruction table row modal submission
  submitInstructionTableRowModal(): void {
    // Use the field reference we stored when opening the modal
    const targetField = this.instructionTableRowField || this.newField;
    
    if (!targetField) {
      console.error('No target field found for instruction table row');
      this.closeInstructionTableRowModal();
      return;
    }
    
    if (!targetField.instructionColumns || targetField.instructionColumns.length === 0) {
      console.error('No instruction columns found');
      this.closeInstructionTableRowModal();
      return;
    }
    
    // Create a new row object with values from the modal
    // Use column index to ensure correct mapping
    const newRow: any = {};
    targetField.instructionColumns.forEach((column: any, index: number) => {
      // Use column.title as the key to match with table display
      const columnKey = column.title;
      if (columnKey) {
        // Get value from modal values, using column title as key
        newRow[columnKey] = this.instructionTableRowValues[columnKey] !== undefined 
          ? this.instructionTableRowValues[columnKey] 
          : (column.type === 'checkbox' ? false : '');
      }
    });
    
    if (!targetField.instructionTableData) {
      targetField.instructionTableData = [];
    }
    
    targetField.instructionTableData.push(newRow);
    console.log('Added new instruction table row:', newRow);
    console.log('Target field columns:', targetField.instructionColumns.map((c: any) => c.title));
    console.log('Modal values:', this.instructionTableRowValues);
    console.log('All instruction table rows:', targetField.instructionTableData);
    
    // Close the modal
    this.closeInstructionTableRowModal();
  }

  // Close instruction table row modal
  closeInstructionTableRowModal(): void {
    this.isInstructionTableRowModalOpen = false;
    this.instructionTableRowValues = {};
    this.instructionTableRowField = null;
  }

  // Remove a row from the instruction table
  removeInstructionTableRow(rowIndex: number): void {
    if (this.newField.instructionTableData && rowIndex >= 0 && rowIndex < this.newField.instructionTableData.length) {
      this.newField.instructionTableData.splice(rowIndex, 1);
      console.log('Removed instruction table row at index:', rowIndex);
      console.log('Remaining instruction table rows:', this.newField.instructionTableData);
    }
  }

  // Add a new row to the table
  addTableRow(fieldName: string): void {
    const tableControl = this.form.get(fieldName);
    if (tableControl) {
      // Find the field configuration to get column definitions
      const field = this.fields.find(f => f.name === fieldName);
      if (!field || !field.columns) {
        console.warn('Field or columns not found for:', fieldName);
        return;
      }
      
      // For fixed-rows-table, open modal to input statement values
      if (field.type === 'fixed-rows-table') {
        const statementColumns = this.getStatementColumns(field);
        if (statementColumns.length > 0) {
          // Open modal to collect statement values
          this.statementModalFieldName = fieldName;
          this.statementModalRowIndex = null; // null means new row
          this.statementModalColumns = statementColumns;
          this.statementModalValues = {};
          // Initialize empty values for each statement column
          statementColumns.forEach((column: any) => {
            const originalIndex = field.columns.indexOf(column);
            const columnKey = column.name || (originalIndex > 0 ? `col_${originalIndex - 1}` : `col_0`);
            this.statementModalValues[columnKey] = '';
          });
          this.isStatementModalOpen = true;
          return;
        }
      }
      
      // For regular tables, initialize with null values
      const newRow: any = {};
      const columnsToUse = field.type === 'fixed-rows-table' 
        ? this.getTableColumnsExcludingSrNo(field)
        : field.columns;
      
      columnsToUse.forEach((column: any, index: number) => {
        const columnKey = column.name || 'col_' + index;
        newRow[columnKey] = null;
      });
      
      const currentData = tableControl.value || [];
      currentData.push(newRow);
      console.log("Current Data:", currentData);
      tableControl.setValue(currentData);
    }
  }

  // Remove a row from the table
  removeTableRow(fieldName: string, rowIndex: number): void {
    const tableControl = this.form.get(fieldName);
    if (!tableControl) {
      return;
    }

    const currentData = tableControl.value || [];
    if (rowIndex < 0 || rowIndex >= currentData.length) {
      return;
    }

    const updatedData = currentData.filter((_: any, index: number) => index !== rowIndex);
    const field = this.fields.find(f => f.name === fieldName);

    if (field && field.type === 'fixed-rows-table') {
      this.submitStatementValuesToBackend(
        field,
        updatedData,
        () => {
          tableControl.setValue(updatedData);
          console.log("Current Data:", updatedData);
        },
        undefined,
        true
      );
      return;
    }

    tableControl.setValue(updatedData);
  }

  // Open statement modal for editing a row
  openEditStatementModal(fieldName: string, rowIndex: number): void {
    const field = this.fields.find(f => f.name === fieldName);
    if (!field || field.type !== 'fixed-rows-table') {
      return;
    }

    const statementColumns = this.getStatementColumns(field);
    if (statementColumns.length === 0) {
      return;
    }

    const tableControl = this.form.get(fieldName);
    if (!tableControl) {
      return;
    }

    const currentData = tableControl.value || [];
    const row = currentData[rowIndex];
    if (!row) {
      return;
    }

    // Open modal with existing values
    this.statementModalFieldName = fieldName;
    this.statementModalRowIndex = rowIndex;
    this.statementModalColumns = statementColumns;
    this.statementModalValues = {};

    // Load existing statement values
    statementColumns.forEach((column: any) => {
      const originalIndex = field.columns.indexOf(column);
      const columnKey = column.name || (originalIndex > 0 ? `col_${originalIndex - 1}` : `col_0`);
      this.statementModalValues[columnKey] = row[columnKey] || '';
    });

    this.isStatementModalOpen = true;
  }

  // Handle statement modal submission
  submitStatementModal(): void {
    // Get the field first
    const field = this.fields.find(f => f.name === this.statementModalFieldName);
    if (!field) {
      console.warn('Field not found');
      console.warn('Field not found');
      console.warn('Field not found');
      this.closeStatementModal();
      return;
    }

    // Validate that all required statement columns have values
    const missingValues: string[] = [];
    this.statementModalColumns.forEach((column: any) => {
      const originalIndex = field.columns.indexOf(column);
      const columnKey = column.name || (originalIndex > 0 ? `col_${originalIndex - 1}` : `col_0`);
      if (column.required && (!this.statementModalValues[columnKey] || this.statementModalValues[columnKey].trim() === '')) {
        missingValues.push(column.title || 'this field');
      }
    });

    if (missingValues.length > 0) {
      alert(`Please enter values for the following required fields: ${missingValues.join(', ')}`);
      return;
    }

    const tableControl = this.form.get(this.statementModalFieldName);
    
    if (!tableControl) {
      console.warn('Table control not found');
      this.closeStatementModal();
      return;
    }

    const currentData = tableControl.value || [];
    const updatedData = (currentData || []).map((row: any) => ({ ...row }));
    const isAddRow = this.statementModalRowIndex === null;
    const currentFormId = this.currentForm?.id;
    const currentFormName = this.currentForm?.name;

    if (this.statementModalRowIndex === null) {
      // Adding a new row
      const newRow: any = {};
      const columnsToUse = this.getTableColumnsExcludingSrNo(field);
      
      // Set statement column values
      this.statementModalColumns.forEach((column: any) => {
        const originalIndex = field.columns.indexOf(column);
        const columnKey = column.name || (originalIndex > 0 ? `col_${originalIndex - 1}` : `col_0`);
        newRow[columnKey] = this.statementModalValues[columnKey]?.trim() || null;
      });

      // Initialize all other columns with null (excluding Sr. No)
      columnsToUse.forEach((column: any, index: number) => {
        const originalIndex = field.columns.indexOf(column);
        const columnKey = column.name || (originalIndex > 0 ? `col_${originalIndex - 1}` : `col_${index}`);
        // Skip statement columns as we've already set them
        if (!this.isStatementColumn(column)) {
          newRow[columnKey] = null;
        }
      });

      updatedData.push(newRow);
    } else {
      // Editing an existing row
      const row = updatedData[this.statementModalRowIndex];
      if (!row) {
        console.warn('Row not found for editing');
        return;
      }
      // Update statement column values
      this.statementModalColumns.forEach((column: any) => {
        const originalIndex = field.columns.indexOf(column);
        const columnKey = column.name || (originalIndex > 0 ? `col_${originalIndex - 1}` : `col_0`);
        row[columnKey] = this.statementModalValues[columnKey]?.trim() || null;
      });
    }

    // Collect all statement column values from all rows and send to API
    this.submitStatementValuesToBackend(field, updatedData, () => {
      this.closeStatementModal();
      tableControl.setValue(updatedData);
      console.log("Current Data:", updatedData);

      if (isAddRow) {
        if (currentFormId) {
          this.loadTemplate(String(currentFormId));
        } else if (currentFormName) {
          this.loadTemplate();
          setTimeout(() => {
            if (currentFormName && this.template) {
              this.openFormFromUrl(currentFormName);
            }
          }, 200);
        } else {
          this.loadTemplate();
        }
      }
    });
  }

  // Submit all statement values to backend
  submitStatementValuesToBackend(
    field: any,
    tableData: any[],
    onSuccess?: () => void,
    onError?: (error: any) => void,
    allowEmpty: boolean = false
  ): void {
    if (!field || field.type !== 'fixed-rows-table' || !tableData) {
      return;
    }
    if (!allowEmpty && tableData.length === 0) {
      return;
    }

    // Ensure field has columns
    if (!field.columns || !Array.isArray(field.columns)) {
      console.warn('Field columns not available');
      return;
    }

    // Get all statement columns
    const statementColumns = this.getStatementColumns(field);
    if (statementColumns.length === 0) {
      return;
    }

    // Resolve field id defensively (some responses use different id keys)
    const resolvedFieldId =
      field.id ||
      field.field_id ||
      field.fieldId ||
      field.fieldID ||
      this.fields.find((f: any) => f.name === field.name)?.id ||
      this.fields.find((f: any) => f.name === field.name)?.field_id;

    if (!resolvedFieldId) {
      console.error('Cannot update statement values: field_id missing', field);
      return;
    }

    // Collect all statement values from all rows (row 1 to latest)
    const statementValues: string[] = [];
    tableData.forEach((row: any) => {
      statementColumns.forEach((column: any) => {
        const originalIndex = field.columns.indexOf(column);
        const columnKey = column.name || (originalIndex > 0 ? `col_${originalIndex - 1}` : `col_0`);
        const value = row[columnKey] || '';
        statementValues.push(String(value));
      });
    });

    // Prepare API payload
    const payload = [{
      id: null, // Will be set by backend if needed, or can be field's ecrf_table id if available
      field_id: resolvedFieldId,
      value: statementValues
    }];

    // Call API to update ecrf_table
    if (this.apiService && this.apiService.updateFixedRowsTableStatementValues) {
      this.apiService.updateFixedRowsTableStatementValues(payload).subscribe({
        next: (response) => {
          console.log('Statement values updated successfully:', response);
          if (onSuccess) {
            onSuccess();
          }
        },
        error: (error) => {
          console.error('Error updating statement values:', error);
          if (onError) {
            onError(error);
          }
        }
      });
    } else {
      console.error('ApiService or updateFixedRowsTableStatementValues method not available');
    }
  }

  private normalizeColumnTypes(field: any): void {
    if (!field || !Array.isArray(field.columns)) {
      return;
    }
    field.columns.forEach((column: any) => {
      if (column && typeof column.type === 'string') {
        column.type = column.type.trim().toLowerCase();
      }
    });
  }

  private isStatementColumn(column: any): boolean {
    return (column?.type ?? '').toString().trim().toLowerCase() === 'statement';
  }

  // Close statement modal
  closeStatementModal(): void {
    this.isStatementModalOpen = false;
    this.statementModalFieldName = '';
    this.statementModalRowIndex = null;
    this.statementModalColumns = [];
    this.statementModalValues = {};
  }

  // Get column key for statement modal (helper method for template)
  getStatementColumnKey(column: any): string {
    const field = this.fields.find(f => f.name === this.statementModalFieldName);
    if (!field) return column.name || 'col_0';
    const originalIndex = field.columns.indexOf(column);
    return column.name || (originalIndex > 0 ? `col_${originalIndex - 1}` : `col_0`);
  }

  // Update table cell value
  updateTableCell(fieldName: string, rowIndex: number, columnName: string, value: any): void {
    const tableControl = this.form.get(fieldName);
    if (tableControl) {
      const currentData = tableControl.value || [];
      if (currentData[rowIndex]) {
        currentData[rowIndex][columnName] = value;
        tableControl.setValue([...currentData]);
      }
    }
  }

  // Get table cell value
  getTableCellValue(fieldName: string, rowIndex: number, columnName: string): any {
    const tableData = this.getTableData(fieldName);
    if (tableData[rowIndex]) {
      const value = tableData[rowIndex][columnName];
      // Return empty string if value is null or undefined
      return value !== null && value !== undefined ? value : '';
    }
    return '';
  }

  // Add a new column to the table field
  addTableColumn(): void {
    if (!this.newField.columns) {
      this.newField.columns = [];
    }
    const newColumn = {
      title: '',
      type: 'text',
      required: false,
      values: []
    };
    this.newField.columns.push(newColumn);
    console.log('Added new table column:', newColumn);
  }

  // Remove a column from the table field
  removeTableColumn(colIndex: number): void {
    const column = this.newField.columns[colIndex];
    // Prevent removal of Sr. No column in fixed-rows-table
    if (this.newField.type === 'fixed-rows-table' && (column.isSrNo || column.name === '_sr_no')) {
      alert('Sr. No column cannot be removed.');
      return;
    }
    // Prevent removal of second column in fixed-rows-table
    if (this.newField.type === 'fixed-rows-table' && (column.isSecondColumn || colIndex === 1)) {
      alert('The second column cannot be removed from a Fixed Rows Table.');
      return;
    }
    console.log("Column to be removed from database:", column);
    this.apiService.deleteTableColumn(this.templateId, column).subscribe(
      response => {
        this.newField.columns.splice(colIndex, 1);
      },
      error => {
        console.error("Error deleting column:", error);
      }
    );
  }

  // Add a dropdown option to a table column
  addDropdownOption(colIndex: number): void {
    if (this.newField.columns && colIndex >= 0 && colIndex < this.newField.columns.length) {
      const column = this.newField.columns[colIndex];
      if (column.type === 'dropdown') {
        if (!column.values) {
          column.values = [];
        }
        // Use a column-specific option text so each column has its own input state
        const optionText = (column.newOptionText || '').trim();
        if (optionText) {
          const newOption = {
            value: optionText.toLowerCase().replace(/\s+/g, '_'),
            label: optionText
          };
          column.values.push(newOption);
          // Clear only this column's input
          column.newOptionText = '';
          console.log(`Added dropdown option to column ${colIndex}:`, newOption);
        }
      }
    }
  }

  // Add a new column to the instruction table
  addInstructionTableColumn(): void {
    if (!this.newField.instructionColumns) {
      this.newField.instructionColumns = [];
    }
    const newColumn = {
      title: '',
      type: 'text',
      values: []
    };
    this.newField.instructionColumns.push(newColumn);
    console.log('Added new instruction table column:', newColumn);
  }

  // Remove a column from the instruction table
  removeInstructionTableColumn(colIndex: number): void {
    if (this.newField.instructionColumns && colIndex >= 0 && colIndex < this.newField.instructionColumns.length) {
      this.newField.instructionColumns.splice(colIndex, 1);
      console.log('Removed instruction table column at index:', colIndex);
    }
  }

  // Add a dropdown option to an instruction table column
  addInstructionDropdownOption(colIndex: number): void {
    if (this.newField.instructionColumns && colIndex >= 0 && colIndex < this.newField.instructionColumns.length) {
      const column = this.newField.instructionColumns[colIndex];
      if (column.type === 'dropdown') {
        if (!column.values) {
          column.values = [];
        }
        const optionText = (column.newOptionText || '').trim();
        if (optionText) {
          const newOption = {
            value: optionText.toLowerCase().replace(/\s+/g, '_'),
            label: optionText
          };
          column.values.push(newOption);
          column.newOptionText = '';
          console.log(`Added dropdown option to instruction column ${colIndex}:`, newOption);
        }
      }
    }
  }

  // Remove a dropdown option from an instruction table column
  removeInstructionDropdownOption(colIndex: number, optIndex: number): void {
    if (this.newField.instructionColumns && colIndex >= 0 && colIndex < this.newField.instructionColumns.length) {
      const column = this.newField.instructionColumns[colIndex];
      if (column.values && optIndex >= 0 && optIndex < column.values.length) {
        column.values.splice(optIndex, 1);
        console.log(`Removed dropdown option ${optIndex} from instruction column ${colIndex}`);
      }
    }
  }

  // Remove a dropdown option from a table column
  removeDropdownOption(colIndex: number, optIndex: number): void {
    if (this.newField.columns && colIndex >= 0 && colIndex < this.newField.columns.length) {
      const column = this.newField.columns[colIndex];
      if (column.values && optIndex >= 0 && optIndex < column.values.length) {
        column.values.splice(optIndex, 1);
        console.log(`Removed dropdown option ${optIndex} from column ${colIndex}`);
      }
    }
  }

  onFolderDrop(event: CdkDragDrop<any[]>): void {
    if (!this.template.folders) return;

    // Don't proceed if the item was dropped in the same position
    if (event.previousIndex === event.currentIndex) return;

    // Move the item in the array
    moveItemInArray(this.template.folders, event.previousIndex, event.currentIndex);

    // Update folder_order for all folders
    this.template.folders.forEach((folder: any, index: number) => {
      folder.folder_order = index;
    });

    // Send API request immediately
    this.sendOrderToBackend();
  }

  onFormDrop(event: CdkDragDrop<any[]>, folder: any): void {
    const containerForms = folder.forms || [];
    if (!Array.isArray(containerForms)) return;

    if (event.previousContainer === event.container) {
      // Same folder: reorder only
      if (event.previousIndex === event.currentIndex) return;
      moveItemInArray(containerForms, event.previousIndex, event.currentIndex);
    } else {
      // Different folder: move form between folders
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    // Normalize form_order for all folders
    this.template.folders.forEach((f: any) => {
      (f.forms || []).forEach((form: any, index: number) => {
        form.form_order = index;
      });
    });

    if (event.previousContainer !== event.container && this.currentForm) {
      const movedForm = containerForms.find((f: any) => f.id === this.currentForm.id);
      if (movedForm) {
        this.selectedFolderId = folder.id;
      }
    }

    this.sendOrderToBackend();
  }

  // Handle field drop event
  onFieldDrop(event: CdkDragDrop<any[]>): void {
    if (!this.fields || !Array.isArray(this.fields)) return;

    // Don't proceed if the item was dropped in the same position
    if (event.previousIndex === event.currentIndex) return;

    // Move the item in the array
    moveItemInArray(this.fields, event.previousIndex, event.currentIndex);

    // Update field_order for all fields
    this.fields.forEach((field: any, index: number) => {
      field.field_order = index;
    });

    // Update the form in the template structure
    if (this.currentForm) {
      this.currentForm.fields = [...this.fields];
    }

    // Send API request immediately
    this.sendFieldOrderToBackend();
  }

  // Send field order to backend immediately
  private sendFieldOrderToBackend(): void {
    if (!this.currentForm || !this.fields || this.fields.length === 0) {
      return;
    }

    // Build current field state
    const fields = this.fields.map((field: any, index: number) => ({
      field_id: field.id || field.name,
      field_order: field.field_order !== undefined ? field.field_order : index
    }));

    // Prepare payload for API
    const payload = {
      template_id: this.templateId,
      form_id: this.currentForm.id.toString(),
      fields: fields
    };

    console.log('Sending field order to backend:', payload);

    // Send API request to reorder fields
    this.apiService.reorderFields(payload).subscribe(
      response => {
        console.log('Field order updated successfully:', response);
        this.saveFormFields(); // Update local state
      },
      error => {
        console.error('Error updating field order:', error);
        alert('Failed to save field order changes. Please try again.');
        // Reload the form to revert to server state
        if (this.currentForm) {
          this.openForm(this.currentForm);
        }
      }
    );
  }

  private revertFolderOrder(event: CdkDragDrop<any[]>): void {
    if (!this.template.folders) return;
    
    // Move the item back to its original position
    moveItemInArray(this.template.folders, event.currentIndex, event.previousIndex);
    
    // Restore original folder_order values
    this.template.folders.forEach((folder: any, index: number) => {
      folder.folder_order = index;
    });
  }

  onDragStarted(): void {
    this.isDragging = true;
    // Close all dropdowns when starting to drag
    this.closeAllDropdowns();
  }

  onDragEnded(): void {
    // Use a small delay to prevent click events from firing after drag
    setTimeout(() => {
      this.isDragging = false;
    }, 100);
  }
// Duplicate Folder Methods
duplicateFolder(folder: any): void {
  this.folderToDuplicate = folder;
  this.duplicateFolderName = folder.name + ' (Copy)';
  this.duplicateFolderNameError = '';
  this.showDuplicateFolderModal = true;
}

closeDuplicateFolderModal(): void {
  this.showDuplicateFolderModal = false;
  this.folderToDuplicate = null;
  this.duplicateFolderName = '';
  this.duplicateFolderNameError = '';
  this.duplicatingFolder = false;
}

confirmDuplicateFolder(): void {
  if (!this.duplicateFolderName.trim()) {
    this.duplicateFolderNameError = 'Folder name is required';
    return;
  }

  if (this.duplicateFolderName.trim().length > 100) {
    this.duplicateFolderNameError = 'Folder name must be less than 100 characters';
    return;
  }

  const validNameRegex = /^[a-zA-Z0-9\s_\.\/{}\+\-\(\)\[\]]+$/;
  if (!validNameRegex.test(this.duplicateFolderName.trim())) {
    this.duplicateFolderNameError = 'Folder name can only contain letters, numbers, spaces, hyphens, underscores, dots, slashes (/), braces {}, plus (+), parentheses (), and brackets [].';
    return;
  }

  const trimmedName = this.duplicateFolderName.trim();
  const existingFolder = this.template.folders.find((folder: any) => 
    folder.name.toLowerCase() === trimmedName.toLowerCase()
  );
  
  if (existingFolder) {
    this.duplicateFolderNameError = 'A folder with this name already exists';
    return;
  }

  this.duplicateFolderNameError = '';
  this.duplicatingFolder = true;

  // ✅ CORRECT: Use the dedicated duplicateFolder API
  this.apiService.duplicateFolder(
    this.templateId,
    this.folderToDuplicate.id,
    this.duplicateFolderName.trim()
  ).subscribe(
    response => {
      console.log('Folder duplicated successfully:', response);
      this.duplicatingFolder = false;
      this.closeDuplicateFolderModal();
      
      // Reload the template to get fresh data with new IDs from backend
      this.loadTemplate();
      
      alert('Folder duplicated successfully!');
    },
    error => {
      console.error('Error duplicating folder:', error);
      this.duplicatingFolder = false;
      
      if (error.status === 401) {
        this.duplicateFolderNameError = 'Authentication failed. Please login again.';
      } else if (error.status === 400) {
        this.duplicateFolderNameError = error.error?.message || 'Invalid folder data.';
      } else if (error.status === 500) {
        this.duplicateFolderNameError = 'Server error. Please try again later.';
      } else {
        this.duplicateFolderNameError = `Failed to duplicate folder: ${error.error?.message || error.message || 'Unknown error'}`;
      }
    }
  );
}

// Duplicate Form Methods
duplicateForm(form: any): void {
  this.formToDuplicate = form;
  this.duplicateFormName = form.name + ' (Copy)';
  this.duplicateFormNameError = '';
  this.duplicateFormFolderError = '';

  // Ensure folders list is up to date for dropdown
  this.loadAvailableFolders();

  // Pre-select the current parent folder of the form
  const parentFolderId = this.getFolderIdByForm(form);
  this.duplicateFormSourceFolderId = parentFolderId ? String(parentFolderId) : null;
  this.duplicateFormTargetFolderIds = parentFolderId ? [parentFolderId] : [];
  this.showDuplicateFormFolderDropdown = false;
  this.showDuplicateFormModal = true;
}

closeDuplicateFormModal(): void {
  this.showDuplicateFormModal = false;
  this.formToDuplicate = null;
  this.duplicateFormName = '';
  this.duplicateFormNameError = '';
  this.duplicatingForm = false;
  this.duplicateFormTargetFolderIds = [];
  this.duplicateFormSourceFolderId = null;
  this.duplicateFormFolderError = '';
  this.showDuplicateFormFolderDropdown = false;
}

confirmDuplicateForm(): void {
  if (!this.duplicateFormName.trim()) {
    this.duplicateFormNameError = 'Form name is required';
    return;
  }

  if (this.duplicateFormName.trim().length > 100) {
    this.duplicateFormNameError = 'Form name must be less than 100 characters';
    return;
  }

  const validNameRegex = /^[a-zA-Z0-9\s\-_\.\/]+$/;
  if (!validNameRegex.test(this.duplicateFormName.trim())) {
    this.duplicateFormNameError = 'Form name can only contain letters, numbers, spaces, hyphens, underscores, dots, and slashes (/).';
    return;
  }

  if (!this.duplicateFormTargetFolderIds || this.duplicateFormTargetFolderIds.length === 0) {
    this.duplicateFormFolderError = 'Please select at least one target folder';
    return;
  }

  const trimmedName = this.duplicateFormName.trim();

  // Validate that the name does not already exist in any selected target folder
  const targetFolders = (this.template?.folders || []).filter((f: any) =>
    this.duplicateFormTargetFolderIds.includes(f.id)
  );

  if (!targetFolders || targetFolders.length === 0) {
    this.duplicateFormFolderError = 'Selected target folder(s) not found';
    return;
  }

  for (const folder of targetFolders) {
    const existingForm = (folder.forms || []).find(
      (form: any) => (form.name || '').trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (existingForm) {
      this.duplicateFormFolderError = `A form with this name already exists in folder "${folder.name}".`;
      return;
    }
  }

  this.duplicateFormFolderError = '';
  this.duplicateFormNameError = '';
  this.duplicatingForm = true;

  // Send one request; include selected folders as `folderIds` array (existing endpoint)
  const sourceFolderId =
    this.duplicateFormSourceFolderId || this.getFolderIdByForm(this.formToDuplicate) || null;
  console.log('[DuplicateForm] submitting duplicate form', {
    templateId: this.templateId,
    sourceFormId: this.formToDuplicate?.id,
    newFormName: trimmedName,
    sourceFolderId,
    folderIds: this.duplicateFormTargetFolderIds
  });
  this.apiService.duplicateForm(
    this.templateId,
    sourceFolderId,
    this.formToDuplicate.id,
    trimmedName,
    this.duplicateFormTargetFolderIds
  ).subscribe(
    response => {
      console.log('Form duplicated successfully into folders:', response);
      this.duplicatingForm = false;
      this.closeDuplicateFormModal();

      this.loadTemplate();
      alert('Form duplicated successfully!');
    },
    error => {
      console.error('Error duplicating form:', error);
      this.duplicatingForm = false;

      if (error.status === 401) {
        this.duplicateFormNameError = 'Authentication failed. Please login again.';
      } else if (error.status === 400) {
        this.duplicateFormNameError = error.error?.message || 'Invalid form data.';
      } else if (error.status === 500) {
        this.duplicateFormNameError = 'Server error. Please try again later.';
      } else {
        this.duplicateFormNameError = `Failed to duplicate form: ${error.error?.message || error.message || 'Unknown error'}`;
      }
    }
  );
}

toggleDuplicateFormFolderDropdown(): void {
  this.showDuplicateFormFolderDropdown = !this.showDuplicateFormFolderDropdown;
}

isDuplicateFormFolderSelected(folderId: string): boolean {
  return this.duplicateFormTargetFolderIds.includes(folderId);
}

onDuplicateFormFolderToggle(folderId: string, checked: boolean): void {
  console.log('[DuplicateForm] folder toggle', { folderId, checked, before: this.duplicateFormTargetFolderIds });
  if (checked) {
    if (!this.duplicateFormTargetFolderIds.includes(folderId)) {
      this.duplicateFormTargetFolderIds = [...this.duplicateFormTargetFolderIds, folderId];
    }
  } else {
    this.duplicateFormTargetFolderIds = this.duplicateFormTargetFolderIds.filter(id => id !== folderId);
  }

  console.log('[DuplicateForm] selected folderIds after toggle:', this.duplicateFormTargetFolderIds);
  if (!this.duplicateFormTargetFolderIds.length) {
    this.duplicateFormFolderError = 'Please select at least one target folder';
  } else {
    this.duplicateFormFolderError = '';
  }
}

getDuplicateFormFolderDropdownLabel(): string {
  if (!this.duplicateFormTargetFolderIds || !this.duplicateFormTargetFolderIds.length) {
    return 'Select folder(s)';
  }

  if (!this.availableFolders || !this.availableFolders.length) {
    return `${this.duplicateFormTargetFolderIds.length} folder(s) selected`;
  }

  const selectedNames = this.availableFolders
    .filter((f: any) => this.duplicateFormTargetFolderIds.includes(f.id))
    .map((f: any) => f.name);

  if (!selectedNames.length) {
    return `${this.duplicateFormTargetFolderIds.length} folder(s) selected`;
  }

  if (selectedNames.length <= 2) {
    return selectedNames.join(', ');
  }

  const [first, second, ...rest] = selectedNames;
  return `${first}, ${second} +${rest.length} more`;
}

// Move Form Methods (and drag-drop helpers)
getFolderForms(folder: any): any[] {
  if (!folder.forms) folder.forms = [];
  return folder.forms;
}

getFormDropListIds(): string[] {
  return (this.template?.folders || []).map((f: any) => 'form-list-' + f.id);
}

getMoveFormTargetFolders(): any[] {
  if (!this.formToMove || !this.template?.folders) return [];
  const sourceFolderId = this.getFolderIdByForm(this.formToMove);
  return this.template.folders.filter((f: any) => f.id !== sourceFolderId);
}

openMoveFormModal(form: any): void {
  this.formToMove = form;
  this.moveFormTargetFolderId = null;
  this.showMoveFormModal = true;
}

closeMoveFormModal(): void {
  this.showMoveFormModal = false;
  this.formToMove = null;
  this.moveFormTargetFolderId = null;
  this.movingForm = false;
}

confirmMoveForm(): void {
  if (!this.formToMove || !this.moveFormTargetFolderId || !this.template?.folders) return;

  const sourceFolder = this.template.folders.find((f: any) =>
    f.forms && f.forms.some((formItem: any) => formItem.id === this.formToMove.id)
  );
  const targetFolder = this.template.folders.find((f: any) => f.id === this.moveFormTargetFolderId);

  if (!sourceFolder || !targetFolder || sourceFolder.id === targetFolder.id) return;

  const formIndex = sourceFolder.forms.findIndex((f: any) => f.id === this.formToMove.id);
  if (formIndex === -1) return;

  const [movedForm] = sourceFolder.forms.splice(formIndex, 1);
  const newOrder = (targetFolder.forms || []).length;
  movedForm.form_order = newOrder;
  if (!targetFolder.forms) targetFolder.forms = [];
  targetFolder.forms.push(movedForm);

  this.template.folders.forEach((folder: any, index: number) => {
    folder.folder_order = index;
    (folder.forms || []).forEach((form: any, formIndex: number) => {
      form.form_order = formIndex;
    });
  });

  const payload = {
    template_id: this.templateId,
    folder: this.template.folders.map((folder: any) => ({
      folder_id: folder.id,
      folder_order: folder.folder_order,
      forms: (folder.forms || []).map((form: any) => ({
        form_id: form.id,
        form_order: form.form_order
      }))
    }))
  };

  this.movingForm = true;
  this.apiService.updateBulkOrder(payload).subscribe(
    () => {
      this.movingForm = false;
      this.closeMoveFormModal();
      if (this.currentForm && this.currentForm.id === this.formToMove.id) {
        this.selectedFolderId = targetFolder.id;
        this.loadTemplate(this.formToMove.id);
      }
    },
    error => {
      this.movingForm = false;
      sourceFolder.forms.splice(formIndex, 0, movedForm);
      targetFolder.forms.pop();
      this.template.folders.forEach((folder: any, index: number) => {
        folder.folder_order = index;
        (folder.forms || []).forEach((form: any, formIndex: number) => {
          form.form_order = formIndex;
        });
      });
      console.error('Error moving form:', error);
      alert('Failed to move form. Please try again.');
    }
  );
}

private buildDuplicateFieldTitle(sourceTitle: string): string {
  const baseTitle = (sourceTitle || 'Field').trim() || 'Field';
  const baseCopyTitle = `${baseTitle} (Copy)`;
  const existingTitles = this.fields.map((field: any) => (field.title || '').toLowerCase());

  if (!existingTitles.includes(baseCopyTitle.toLowerCase())) {
    return baseCopyTitle;
  }

  let counter = 2;
  let candidate = `${baseTitle} (Copy ${counter})`;
  while (existingTitles.includes(candidate.toLowerCase())) {
    counter += 1;
    candidate = `${baseTitle} (Copy ${counter})`;
  }
  return candidate;
}

duplicateField(index: number): void {
  const field = this.fields[index];
  if (!field || !this.currentForm || !this.templateId) {
    alert('Unable to duplicate field: Missing required information');
    return;
  }

  const newFieldTitle = this.buildDuplicateFieldTitle(field.title);
  this.loading = true;

  // Close the options modal
  this.isOptionsModalOpen = false;
  this.clickedFieldIndex = null;

  // Store current form info to reopen it after refetch
  const currentFormId = this.currentForm?.id;
  const currentFormName = this.currentForm?.name;
  const currentUrl = this.router.url;
  console.log('Storing form info for reopening:', { id: currentFormId, name: currentFormName, url: currentUrl });

  this.apiService.duplicateField(
    this.templateId,
    this.currentForm.id,
    field.id,
    newFieldTitle
  ).subscribe(
    response => {
      console.log('Field duplicated successfully:', response);
      this.loading = false;

      if (currentFormId) {
        this.loadTemplate(String(currentFormId));
      } else if (currentFormName) {
        this.loadTemplate();
        setTimeout(() => {
          if (currentFormName && this.template) {
            this.openFormFromUrl(currentFormName);
          }
        }, 200);
      } else {
        this.loadTemplate();
      }

      alert('Field duplicated successfully!');
    },
    error => {
      console.error('Error duplicating field:', error);
      this.loading = false;
      alert(error.error?.message || 'Failed to duplicate field. Please try again.');
    }
  );
}

}